import { useState, useEffect, useMemo, useCallback } from "react";

// ============================================================
// CONFIGURACIÓN DE SUPABASE
// Cambia estos valores por los de tu proyecto en supabase.com
// ============================================================
const SUPABASE_URL = "https://ucikuilbjitvluphqfph.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_U9TKrT46rVLp9j4pZZg-3w_Xc_4Oz4P";

// ============================================================
// MINI SUPABASE CLIENT (sin dependencias externas)
// ============================================================
class SupabaseClient {
  constructor(url, key) {
    this.url = url;
    this.key = key;
    this.token = null;
    this.user = null;
  }
  headers() {
    const h = {
      apikey: this.key,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    };
    if (this.token) h.Authorization = `Bearer ${this.token}`;
    return h;
  }
  async signIn(email, password) {
    const r = await fetch(`${this.url}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: this.key, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json();
    if (d.access_token) {
      this.token = d.access_token;
      this.user = d.user;
    }
    return d;
  }
  async signUp(email, password) {
    const r = await fetch(`${this.url}/auth/v1/signup`, {
      method: "POST",
      headers: { apikey: this.key, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return r.json();
  }
  signOut() {
    this.token = null;
    this.user = null;
  }
  from(table) {
    const self = this;
    return {
      select(columns = "*") {
        const chain = { _filters: [], _order: null, _single: false };
        chain.eq = (col, val) => {
          chain._filters.push(`${col}=eq.${val}`);
          return chain;
        };
        chain.gte = (col, val) => {
          chain._filters.push(`${col}=gte.${val}`);
          return chain;
        };
        chain.lte = (col, val) => {
          chain._filters.push(`${col}=lte.${val}`);
          return chain;
        };
        chain.in = (col, vals) => {
          chain._filters.push(`${col}=in.(${vals.join(",")})`);
          return chain;
        };
        chain.order = (col, { ascending = true } = {}) => {
          chain._order = `${col}.${ascending ? "asc" : "desc"}`;
          return chain;
        };
        chain.single = () => {
          chain._single = true;
          return chain;
        };
        chain.then = async (resolve, reject) => {
          try {
            let url = `${self.url}/rest/v1/${table}?select=${columns}`;
            chain._filters.forEach((f) => (url += `&${f}`));
            if (chain._order) url += `&order=${chain._order}`;
            const r = await fetch(url, { headers: self.headers() });
            const d = await r.json();
            resolve({
              data: chain._single ? d[0] || null : d,
              error: r.ok ? null : d,
            });
          } catch (e) {
            reject ? reject(e) : resolve({ data: null, error: e });
          }
        };
        return chain;
      },
      async insert(rows) {
        const r = await fetch(`${self.url}/rest/v1/${table}`, {
          method: "POST",
          headers: self.headers(),
          body: JSON.stringify(Array.isArray(rows) ? rows : [rows]),
        });
        const d = await r.json();
        return { data: d, error: r.ok ? null : d };
      },
      update(data) {
        const chain = { _filters: [] };
        chain.eq = (col, val) => {
          chain._filters.push(`${col}=eq.${val}`);
          return chain;
        };
        chain.then = async (resolve) => {
          let url = `${self.url}/rest/v1/${table}?`;
          chain._filters.forEach((f, i) => (url += `${i ? "&" : ""}${f}`));
          const r = await fetch(url, {
            method: "PATCH",
            headers: self.headers(),
            body: JSON.stringify(data),
          });
          const d = await r.json();
          resolve({ data: d, error: r.ok ? null : d });
        };
        return chain;
      },
      delete() {
        const chain = { _filters: [] };
        chain.eq = (col, val) => {
          chain._filters.push(`${col}=eq.${val}`);
          return chain;
        };
        chain.then = async (resolve) => {
          let url = `${self.url}/rest/v1/${table}?`;
          chain._filters.forEach((f, i) => (url += `${i ? "&" : ""}${f}`));
          const r = await fetch(url, {
            method: "DELETE",
            headers: self.headers(),
          });
          resolve({ data: null, error: r.ok ? null : await r.json() });
        };
        return chain;
      },
    };
  }
  async rpc(fn, params = {}) {
    const r = await fetch(`${self.url}/rest/v1/rpc/${fn}`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(params),
    });
    return { data: await r.json(), error: r.ok ? null : true };
  }
}

// ============================================================
// HELPERS
// ============================================================
const fmt = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(n || 0);
const daysUntil = (dateStr) => {
  if (!dateStr) return Infinity;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
};
const fmtDate = (d) =>
  d
    ? new Date(d + "T12:00:00").toLocaleDateString("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

// ============================================================
// STYLES — THEME SYSTEM
// ============================================================
const darkColors = {
  bg: "#08080f",
  bgCard: "#0e0e1a",
  bgInput: "#12122a",
  border: "#1e1e3a",
  borderFocus: "#4f46e5",
  text: "#e8e8f0",
  textMuted: "#6b6b8a",
  textDim: "#3a3a5a",
  primary: "#6366f1",
  primaryHover: "#818cf8",
  accent: "#22d3ee",
  green: "#10b981",
  greenBg: "#052e16",
  greenBorder: "#064e3b",
  red: "#ef4444",
  redBg: "#1c0a0a",
  redBorder: "#450a0a",
  yellow: "#f59e0b",
  yellowBg: "#1c1500",
  yellowBorder: "#451a03",
  purple: "#a78bfa",
  purpleBg: "#1a0a2e",
  purpleBorder: "#2e1065",
  blue: "#60a5fa",
  headerBg: "linear-gradient(135deg, #08080f, #0f0f28)",
  modalOverlay: "rgba(0,0,0,0.7)",
  shadow: "none",
};

const lightColors = {
  bg: "#f9fafb",
  bgCard: "#ffffff",
  bgInput: "#ffffff",
  border: "#e5e7eb",
  borderFocus: "#6366f1",
  text: "#111827",
  textMuted: "#6b7280",
  textDim: "#9ca3af",
  primary: "#6366f1",
  primaryHover: "#4f46e5",
  accent: "#0891b2",
  green: "#059669",
  greenBg: "#ecfdf5",
  greenBorder: "#a7f3d0",
  red: "#dc2626",
  redBg: "#fef2f2",
  redBorder: "#fecaca",
  yellow: "#d97706",
  yellowBg: "#fffbeb",
  yellowBorder: "#fde68a",
  purple: "#7c3aed",
  purpleBg: "#f5f3ff",
  purpleBorder: "#ddd6fe",
  blue: "#2563eb",
  headerBg: "#ffffff",
  modalOverlay: "rgba(0,0,0,0.25)",
  shadow: "0 1px 3px rgba(0,0,0,0.08)",
};

// Mutable references — components read from these, App() updates them on theme change
let colors = { ...darkColors };
let S = {};

const updateStyles = (isDark) => {
  Object.assign(colors, isDark ? darkColors : lightColors);
  S.input = {
    width: "100%",
    padding: "10px 14px",
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    color: colors.text,
    fontSize: 13,
    outline: "none",
    fontFamily: "'Outfit', sans-serif",
    transition: "border-color 0.2s",
  };
  S.label = {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: colors.textMuted,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 1,
  };
  S.btn = {
    padding: "10px 20px",
    borderRadius: 10,
    border: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    transition: "all 0.2s",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  };
  S.btnPrimary = {
    background: `linear-gradient(135deg, ${colors.primary}, #4338ca)`,
    color: "#fff",
  };
  S.btnGhost = {
    background: "transparent",
    border: `1px solid ${colors.border}`,
    color: colors.textMuted,
  };
  S.btnDanger = {
    background: colors.redBg,
    border: `1px solid ${colors.redBorder}`,
    color: colors.red,
  };
  S.card = {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: isDark ? 16 : 14,
    padding: 24,
    boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
  };
};
updateStyles(false); // Initialize with dark theme

// ============================================================
// REUSABLE COMPONENTS
// ============================================================
const Badge = ({ type, text }) => {
  const map = {
    active: {
      bg: colors.greenBg,
      color: colors.green,
      border: colors.greenBorder,
    },
    expiring: {
      bg: colors.yellowBg,
      color: colors.yellow,
      border: colors.yellowBorder,
    },
    expired: { bg: colors.redBg, color: colors.red, border: colors.redBorder },
    vacant: {
      bg: colors.purpleBg,
      color: colors.purple,
      border: colors.purpleBorder,
    },
    paid: {
      bg: colors.greenBg,
      color: colors.green,
      border: colors.greenBorder,
    },
    overdue: { bg: colors.redBg, color: colors.red, border: colors.redBorder },
    pending: {
      bg: colors.yellowBg,
      color: colors.yellow,
      border: colors.yellowBorder,
    },
    partial: {
      bg: colors.yellowBg,
      color: colors.yellow,
      border: colors.yellowBorder,
    },
    scheduled: {
      bg: colors.purpleBg,
      color: colors.purple,
      border: colors.purpleBorder,
    },
    in_progress: {
      bg: colors.purpleBg,
      color: colors.blue,
      border: colors.purpleBorder,
    },
    completed: {
      bg: colors.greenBg,
      color: colors.green,
      border: colors.greenBorder,
    },
  };
  const c = map[type] || map.active;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 20,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

const KPI = ({ label, value, sub, color, icon, isDark, isMobile }) => (
  <div style={{ ...S.card, padding: isMobile ? 12 : 24, overflow: "hidden" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: isMobile ? 6 : 12,
      }}
    >
      {isDark && icon ? (
        <span style={{ fontSize: isMobile ? 14 : 18 }}>{icon}</span>
      ) : (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: color || colors.textMuted,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          fontSize: isMobile ? 8 : 11,
          color: color || colors.textMuted,
          textTransform: "uppercase",
          letterSpacing: isMobile ? 0.8 : 1.5,
          fontWeight: isDark ? 700 : 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
    </div>
    <div
      style={{
        fontSize: isMobile ? 18 : 32,
        fontWeight: 800,
        color: colors.text,
        fontFamily: "'Outfit', sans-serif",
        letterSpacing: -1,
        lineHeight: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </div>
    {sub && !isMobile && (
      <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>
        {sub}
      </div>
    )}
  </div>
);

const Modal = ({ open, onClose, title, children, width = 560 }) => {
  if (!open) return null;
  const mobile = window.innerWidth < 768;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: colors.modalOverlay,
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: mobile ? "flex-end" : "center",
        justifyContent: "center",
        padding: mobile ? 0 : 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          ...S.card,
          width: mobile ? "100%" : width,
          maxWidth: mobile ? "100%" : "95vw",
          maxHeight: mobile ? "90vh" : "85vh",
          overflow: "auto",
          border: `1px solid ${colors.borderFocus}`,
          borderRadius: mobile ? "16px 16px 0 0" : 16,
          padding: mobile ? 20 : 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: colors.text,
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: colors.textMuted,
              fontSize: 20,
              cursor: "pointer",
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, type = "text", placeholder, ...rest }) => (
  <input
    type={type}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={S.input}
    onFocus={(e) => (e.target.style.borderColor = colors.borderFocus)}
    onBlur={(e) => (e.target.style.borderColor = colors.border)}
    {...rest}
  />
);

const Select = ({ value, onChange, options, placeholder }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    style={{ ...S.input, cursor: "pointer" }}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const Tab = ({ active, label, onClick, badge, isDark }) => (
  <button
    onClick={onClick}
    style={{
      background: isDark
        ? active
          ? `linear-gradient(135deg, ${colors.primary}20, ${colors.primary}10)`
          : "transparent"
        : "transparent",
      border: isDark
        ? active
          ? `1px solid ${colors.primary}50`
          : "1px solid transparent"
        : "none",
      borderBottom: !isDark
        ? active
          ? `2px solid ${colors.text}`
          : "2px solid transparent"
        : undefined,
      color: active
        ? isDark
          ? colors.primaryHover
          : colors.text
        : colors.textMuted,
      padding: isDark ? "8px 18px" : "8px 16px 10px",
      borderRadius: isDark ? 10 : 0,
      fontSize: 13,
      fontWeight: active ? 600 : 500,
      cursor: "pointer",
      fontFamily: "'Outfit', sans-serif",
      display: "flex",
      alignItems: "center",
      gap: 8,
      transition: "all 0.2s",
    }}
  >
    {label}
    {badge != null && (
      <span
        style={{
          background: isDark
            ? active
              ? `${colors.primary}30`
              : `${colors.text}10`
            : active
            ? `${colors.text}10`
            : `${colors.textMuted}15`,
          padding: "1px 7px",
          borderRadius: 8,
          fontSize: 11,
        }}
      >
        {badge}
      </span>
    )}
  </button>
);

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authError, setAuthError] = useState("");

  // Data state
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [workorders, setWorkorders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [activeTab, setActiveTab] = useState("overview");
  const [modal, setModal] = useState(null); // { type: 'unit'|'tenant'|'payment'|'maintenance', data: ... }
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [toast, setToast] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [maintFilter, setMaintFilter] = useState("all");
  const [maintMonth, setMaintMonth] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );
  const [maintCat, setMaintCat] = useState("all");
  const [paymentMonth, setPaymentMonth] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );

  // Supabase client
  const [sb] = useState(
    () => new SupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  );
  const isAdmin = sb?.user?.id === "6bcc98c5-a4c2-4f41-b674-7af7c2b1c470";
  const isMaintUser =
    (sb?.user?.email || authEmail || "").toLowerCase() ===
    "rgomez.dcarquitectura@gmail.com";
  const [userRole, setUserRole] = useState("admin"); // "admin" | "readonly" | "maintenance"

  // Update theme
  updateStyles(isDark);

  // Privacy-aware currency formatter
  const $fmt = (n) => (privacyMode ? "$•••••" : fmt(n));

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ─── Data Loading ───
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t, c, p, m, w, proj] = await Promise.all([
        sb.from("units").select("*"),
        sb.from("tenants").select("*"),
        sb.from("contracts").select("*"),
        sb.from("payments").select("*"),
        sb.from("maintenance").select("*"),
        sb.from("workorders").select("*"),
        sb.from("projects").select("*"),
      ]);
      if (u.data) setUnits(u.data);
      if (t.data) setTenants(t.data);
      if (c.data) setContracts(c.data);
      if (p.data) setPayments(p.data);
      if (m.data) setMaintenance(m.data);
      if (w.data) setWorkorders(w.data);
      if (proj.data) setProjects(proj.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [sb]);

  useEffect(() => {
    if (isLoggedIn) loadData();
  }, [isLoggedIn, loadData]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─── Computed Data ───
  // Helper: get the current expected period based on contract frequency
  const getPaymentStatus = (contract, unitPayments) => {
    if (!contract) return null;
    const freq = contract.payment_frequency || "monthly";
    const start = new Date(contract.start_date + "T12:00:00");
    const now = new Date();

    if (freq === "annual") {
      // Annual: next payment due on contract anniversary
      let nextDue = new Date(start);
      while (nextDue <= now) {
        nextDue.setFullYear(nextDue.getFullYear() + 1);
      }
      // Previous due was one year before nextDue
      const prevDue = new Date(nextDue);
      prevDue.setFullYear(prevDue.getFullYear() - 1);
      const expectedPeriod = `${prevDue.getFullYear()}-${String(
        prevDue.getMonth() + 1
      ).padStart(2, "0")}`;
      const hasPaid = unitPayments.some(
        (p) =>
          p.period === expectedPeriod &&
          (p.status === "paid" || p.status === "partial")
      );
      // If prevDue is before contract start, they haven't owed yet
      if (prevDue < start) return { status: "paid", label: "Al corriente" };
      return hasPaid
        ? { status: "paid", label: "Al corriente" }
        : { status: "overdue", label: "Adeudo" };
    }

    if (freq === "semi_annual") {
      let nextDue = new Date(start);
      while (nextDue <= now) {
        nextDue.setMonth(nextDue.getMonth() + 6);
      }
      const prevDue = new Date(nextDue);
      prevDue.setMonth(prevDue.getMonth() - 6);
      const expectedPeriod = `${prevDue.getFullYear()}-${String(
        prevDue.getMonth() + 1
      ).padStart(2, "0")}`;
      if (prevDue < start) return { status: "paid", label: "Al corriente" };
      const hasPaid = unitPayments.some(
        (p) =>
          p.period === expectedPeriod &&
          (p.status === "paid" || p.status === "partial")
      );
      return hasPaid
        ? { status: "paid", label: "Al corriente" }
        : { status: "overdue", label: "Adeudo" };
    }

    if (freq === "quarterly") {
      let nextDue = new Date(start);
      while (nextDue <= now) {
        nextDue.setMonth(nextDue.getMonth() + 3);
      }
      const prevDue = new Date(nextDue);
      prevDue.setMonth(prevDue.getMonth() - 3);
      const expectedPeriod = `${prevDue.getFullYear()}-${String(
        prevDue.getMonth() + 1
      ).padStart(2, "0")}`;
      if (prevDue < start) return { status: "paid", label: "Al corriente" };
      const hasPaid = unitPayments.some(
        (p) =>
          p.period === expectedPeriod &&
          (p.status === "paid" || p.status === "partial")
      );
      return hasPaid
        ? { status: "paid", label: "Al corriente" }
        : { status: "overdue", label: "Adeudo" };
    }

    // Monthly (default)
    // First check if there are ANY overdue payments for this unit
    const hasOverdue = unitPayments.some((p) => p.status === "overdue");
    if (hasOverdue) return { status: "overdue", label: "Adeudo" };

    const currentPeriod = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`;
    const currentPayment = unitPayments.find((p) => p.period === currentPeriod);
    if (!currentPayment) return { status: "pending", label: "Pendiente" };
    return {
      status: currentPayment.status,
      label: {
        paid: "Al corriente",
        overdue: "Adeudo",
        pending: "Pendiente",
        partial: "Parcial",
      }[currentPayment.status],
    };
  };

  const enrichedUnits = useMemo(() => {
    return units
      .map((u) => {
        const tenant = tenants.find(
          (t) => t.unit_id === u.id && t.status === "active"
        );
        const contract = tenant
          ? contracts.find(
              (c) =>
                c.unit_id === u.id &&
                c.tenant_id === tenant.id &&
                c.status === "active"
            )
          : null;
        const unitPayments = payments
          .filter((p) => p.unit_id === u.id)
          .sort((a, b) => b.period.localeCompare(a.period));
        const daysLeft = contract ? daysUntil(contract.end_date) : Infinity;
        let status = "vacant";
        if (tenant && contract) {
          if (daysLeft <= 0) status = "expired";
          else if (daysLeft <= 60) status = "expiring";
          else status = "active";
        }
        const paymentSt = getPaymentStatus(contract, unitPayments);
        const allTenants = tenants.filter((t) => t.unit_id === u.id);
        const allContracts = contracts.filter((c) => c.unit_id === u.id);
        return {
          ...u,
          tenant,
          contract,
          payments: unitPayments,
          allTenants,
          allContracts,
          status,
          daysLeft,
          paymentSt,
        };
      })
      .sort((a, b) => {
        const floorOrder = { PB: 0, 1: 1, 2: 2, 3: 3 };
        return (
          (floorOrder[a.floor] || 0) - (floorOrder[b.floor] || 0) ||
          a.code.localeCompare(b.code)
        );
      });
  }, [units, tenants, contracts, payments]);

  const totalMonthlyRent = enrichedUnits
    .filter((u) => u.contract)
    .reduce((s, u) => s + (u.contract?.monthly_rent || 0), 0);
  const occupiedCount = enrichedUnits.filter((u) => u.tenant).length;
  const overduePayments = payments.filter((p) => p.status === "overdue");
  const totalOverdue = overduePayments.reduce((s, p) => s + p.amount, 0);
  const totalMaintCost = maintenance.reduce((s, m) => s + m.cost, 0);
  const expiringContracts = enrichedUnits
    .filter((u) => u.status === "expiring")
    .sort((a, b) => a.daysLeft - b.daysLeft);

  // Clean empty strings to null for DB compatibility
  const cleanData = (obj) => {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      cleaned[key] = value === "" ? null : value;
    }
    return cleaned;
  };

  // ─── CRUD Handlers ───
  const saveUnit = async (data) => {
    // Solo enviar columnas reales de la tabla units (ignorar campos computados de enrichedUnits)
    const allowedFields = ["id", "code", "type", "floor", "sqm", "base_rent"];
    const filtered = {};
    for (const key of allowedFields) {
      if (data[key] !== undefined) filtered[key] = data[key];
    }
    const clean = cleanData(filtered);
    let result;
    if (clean.id) {
      const { id, ...rest } = clean;
      result = await sb.from("units").update(rest).eq("id", id);
    } else {
      result = await sb.from("units").insert(clean);
    }
    if (result?.error) {
      console.error("Unit save error:", result.error);
      showToast(
        "Error: " + (result.error.message || JSON.stringify(result.error))
      );
      return;
    }
    await loadData();
    showToast("Unidad guardada");
    setModal(null);
  };

  const saveTenant = async (data) => {
    const clean = cleanData(data);
    let result;
    if (clean.id) {
      const { id, ...rest } = clean;
      result = await sb.from("tenants").update(rest).eq("id", id);
    } else {
      result = await sb.from("tenants").insert({ ...clean, status: "active" });
    }
    if (result?.error) {
      console.error("Tenant save error:", result.error);
      showToast(
        "Error: " + (result.error.message || JSON.stringify(result.error))
      );
      return;
    }
    await loadData();
    showToast("Inquilino guardado");
    setModal(null);
  };

  const saveContract = async (data) => {
    const clean = cleanData(data);
    let result;
    if (clean.id) {
      const { id, ...rest } = clean;
      result = await sb.from("contracts").update(rest).eq("id", id);
    } else {
      result = await sb
        .from("contracts")
        .insert({ ...clean, status: "active" });
    }
    if (result?.error) {
      console.error("Contract save error:", result.error);
      showToast(
        "Error: " + (result.error.message || JSON.stringify(result.error))
      );
      return;
    }
    await loadData();
    showToast("Contrato guardado");
    setModal(null);
  };

  const savePayment = async (data) => {
    const clean = cleanData(data);
    let result;
    if (clean.id) {
      const { id, ...rest } = clean;
      result = await sb.from("payments").update(rest).eq("id", id);
    } else {
      result = await sb.from("payments").insert(clean);
    }
    if (result?.error) {
      console.error("Payment save error:", result.error);
      showToast(
        "Error: " +
          (result.error.message ||
            result.error.details ||
            JSON.stringify(result.error))
      );
      return;
    }
    await loadData();
    showToast("Pago registrado");
    setModal(null);
  };

  const generateMonthlyPayments = async () => {
    const period = paymentMonth;
    const [periodYear, periodMonth] = period.split("-").map(Number);
    const activeContracts = contracts.filter((c) => c.status === "active");
    const existingForMonth = payments.filter((p) => p.period === period);
    const unitsWithPayment = new Set(existingForMonth.map((p) => p.unit_id));

    const eligibleContracts = activeContracts.filter((c) => {
      if (unitsWithPayment.has(c.unit_id)) return false;

      const freq = c.payment_frequency || "monthly";

      if (freq === "monthly") return true;

      if (freq === "annual") {
        const startMonth = new Date(c.start_date + "T12:00:00").getMonth() + 1;
        return periodMonth === startMonth;
      }

      if (freq === "semi_annual") {
        const startMonth = new Date(c.start_date + "T12:00:00").getMonth() + 1;
        return (
          periodMonth === startMonth ||
          periodMonth === ((startMonth + 5) % 12) + 1
        );
      }

      return true;
    });

    const newPayments = eligibleContracts.map((c) => {
      const freq = c.payment_frequency || "monthly";
      let amount = c.monthly_rent;
      if (freq === "annual") amount = c.monthly_rent * 12;
      if (freq === "semi_annual") amount = c.monthly_rent * 6;

      return {
        contract_id: c.id,
        tenant_id: c.tenant_id,
        unit_id: c.unit_id,
        period,
        amount,
        due_date: `${period}-01`,
        payment_date: null,
        status: "pending",
      };
    });

    if (newPayments.length === 0) {
      showToast("No hay pagos nuevos por generar para este mes", "error");
      return;
    }

    const { error } = await sb.from("payments").insert(newPayments);
    if (error) {
      showToast("Error al generar pagos", "error");
      return;
    }
    await loadData();
    showToast(`${newPayments.length} pagos pendientes generados`);
  };

  const saveMaintenance = async (data) => {
    const clean = cleanData(data);
    if (clean.id) {
      const { id, ...rest } = clean;
      await sb.from("maintenance").update(rest).eq("id", id);
    } else {
      await sb.from("maintenance").insert(clean);
    }
    await loadData();
    showToast("Gasto guardado");
    setModal(null);
  };

  const saveWorkorder = async (data) => {
    const clean = cleanData(data);
    if (clean.id) {
      const { id, ...rest } = clean;
      await sb.from("workorders").update(rest).eq("id", id);
    } else {
      await sb.from("workorders").insert(clean);
    }
    await loadData();
    showToast("Trabajo guardado");
    setModal(null);
  };

  const saveProject = async (data) => {
    const clean = cleanData(data);
    if (clean.id) {
      const { id, ...rest } = clean;
      await sb.from("projects").update(rest).eq("id", id);
    } else {
      await sb.from("projects").insert(clean);
    }
    await loadData();
    showToast("Proyecto guardado");
    setModal(null);
  };

  // Valor catastral del edificio (editable por admin)
  const [catastralValue, setCatastralValue] = useState(23753790);

  // ─── LOGIN SCREEN ───
  if (!isLoggedIn) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: colors.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Outfit', sans-serif",
          transition: "background 0.3s ease",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <style>{"* { box-sizing: border-box; }"}</style>
        {/* Theme toggle on login */}
        <button
          onClick={() => setIsDark(!isDark)}
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            background: "transparent",
            border: `1px solid ${colors.border}`,
            borderRadius: 20,
            width: 38,
            height: 38,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            zIndex: 10,
          }}
        >
          {isDark ? "🌙" : "☀️"}
        </button>
        <div style={{ ...S.card, width: 380, maxWidth: "90vw", padding: 32 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <img
              src="/TA.png"
              alt="Turcott Asociados"
              style={{ height: 60, objectFit: "contain", marginBottom: 8 }}
            />
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: colors.text,
              }}
            >
              Av. Universidad 1821
            </h1>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                color: colors.textMuted,
              }}
            >
              Inicia sesión para continuar
            </p>
          </div>
          {authError && (
            <div
              style={{
                background: colors.redBg,
                border: `1px solid ${colors.redBorder}`,
                borderRadius: 10,
                padding: "8px 12px",
                marginBottom: 16,
                fontSize: 12,
                color: colors.red,
              }}
            >
              {authError}
            </div>
          )}
          <Field label="Email">
            <input
              type="email"
              value={authEmail || ""}
              onChange={(e) => setAuthEmail(e.target.value)}
              placeholder="tu@email.com"
              style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
            />
          </Field>
          <Field label="Contraseña">
            <input
              type="password"
              value={authPass || ""}
              onChange={(e) => setAuthPass(e.target.value)}
              placeholder="••••••••"
              style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
            />
          </Field>
          <button
            onClick={async () => {
              setAuthError("");
              try {
                const res = await sb.signIn(authEmail, authPass);
                if (res.access_token) {
                  // Determine role based on user
                  const email = (
                    sb?.user?.email ||
                    authEmail ||
                    ""
                  ).toLowerCase();
                  const uid = sb?.user?.id;
                  if (uid === "6bcc98c5-a4c2-4f41-b674-7af7c2b1c470") {
                    setUserRole("admin");
                    setActiveTab("overview");
                  } else if (email === "rgomez.dcarquitectura@gmail.com") {
                    setUserRole("maintenance");
                    setActiveTab("workorders");
                  } else {
                    setUserRole("readonly");
                    setActiveTab("overview");
                  }
                  setIsLoggedIn(true);
                } else {
                  setAuthError(
                    res.error_description || res.msg || "Credenciales inválidas"
                  );
                }
              } catch (e) {
                setAuthError("No se pudo conectar al servidor.");
              }
            }}
            style={{
              ...S.btn,
              ...S.btnPrimary,
              width: "100%",
              justifyContent: "center",
              marginTop: 8,
            }}
          >
            Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  // ─── FORM MODALS ───
  const renderModal = () => {
    if (!modal) return null;

    if (modal.type === "unit") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      return (
        <Modal
          open
          title={form.id ? "Editar Unidad" : "Nueva Unidad"}
          onClose={() => setModal(null)}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Código">
              <Input
                value={form.code}
                onChange={(v) => setForm((f) => ({ ...f, code: v }))}
                placeholder="Ej: L-01, OF-201"
              />
            </Field>
            <Field label="Tipo">
              <Select
                value={form.type}
                onChange={(v) => setForm((f) => ({ ...f, type: v }))}
                options={[
                  { value: "Local", label: "Local" },
                  { value: "Oficina", label: "Oficina" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Piso">
              <Select
                value={form.floor}
                onChange={(v) => setForm((f) => ({ ...f, floor: v }))}
                options={[
                  { value: "PB", label: "Planta Baja" },
                  { value: "1", label: "Piso 1" },
                  { value: "2", label: "Piso 2" },
                  { value: "3", label: "Piso 3" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="m²">
              <Input
                value={form.sqm}
                onChange={(v) =>
                  setForm((f) => ({ ...f, sqm: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
            <Field label="Renta base">
              <Input
                value={form.base_rent}
                onChange={(v) =>
                  setForm((f) => ({ ...f, base_rent: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <button
              onClick={() => setModal(null)}
              style={{ ...S.btn, ...S.btnGhost }}
            >
              Cancelar
            </button>
            <button
              onClick={() => saveUnit(form)}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "tenant") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      const vacantUnits = enrichedUnits.filter(
        (u) => u.status === "vacant" || u.id === form.unit_id
      );
      return (
        <Modal
          open
          title={form.id ? "Editar Inquilino" : "Nuevo Inquilino"}
          onClose={() => setModal(null)}
        >
          <Field label="Unidad">
            <Select
              value={form.unit_id}
              onChange={(v) => setForm((f) => ({ ...f, unit_id: v }))}
              options={vacantUnits.map((u) => ({
                value: u.id,
                label: `${u.code} — ${u.type} (${u.sqm}m²)`,
              }))}
              placeholder="Seleccionar unidad"
            />
          </Field>
          <Field label="Nombre del negocio/inquilino">
            <Input
              value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              placeholder="Ej: Despacho Jurídico MR"
            />
          </Field>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Persona de contacto">
              <Input
                value={form.contact_name}
                onChange={(v) => setForm((f) => ({ ...f, contact_name: v }))}
              />
            </Field>
            <Field label="Teléfono">
              <Input
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
            </Field>
            <Field label="Email">
              <Input
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                type="email"
              />
            </Field>
            <Field label="RFC">
              <Input
                value={form.rfc}
                onChange={(v) => setForm((f) => ({ ...f, rfc: v }))}
              />
            </Field>
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <button
              onClick={() => setModal(null)}
              style={{ ...S.btn, ...S.btnGhost }}
            >
              Cancelar
            </button>
            <button
              onClick={() => saveTenant(form)}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "contract") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      const activeTenantsList = tenants.filter((t) => t.status === "active");
      return (
        <Modal
          open
          title={form.id ? "Editar Contrato" : "Nuevo Contrato"}
          onClose={() => setModal(null)}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Inquilino">
              <Select
                value={form.tenant_id}
                onChange={(v) => {
                  const t = tenants.find((t) => t.id === v);
                  setForm((f) => ({
                    ...f,
                    tenant_id: v,
                    unit_id: t?.unit_id || f.unit_id,
                  }));
                }}
                options={activeTenantsList.map((t) => ({
                  value: t.id,
                  label: `${t.name} (${
                    units.find((u) => u.id === t.unit_id)?.code || "?"
                  })`,
                }))}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Unidad">
              <Select
                value={form.unit_id}
                onChange={(v) => setForm((f) => ({ ...f, unit_id: v }))}
                options={units.map((u) => ({ value: u.id, label: u.code }))}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Inicio">
              <Input
                value={form.start_date}
                onChange={(v) => setForm((f) => ({ ...f, start_date: v }))}
                type="date"
              />
            </Field>
            <Field label="Fin">
              <Input
                value={form.end_date}
                onChange={(v) => setForm((f) => ({ ...f, end_date: v }))}
                type="date"
              />
            </Field>
            <Field label="Renta">
              <Input
                value={form.monthly_rent}
                onChange={(v) =>
                  setForm((f) => ({ ...f, monthly_rent: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
            <Field label="Frecuencia de pago">
              <Select
                value={form.payment_frequency || "monthly"}
                onChange={(v) =>
                  setForm((f) => ({ ...f, payment_frequency: v }))
                }
                options={[
                  { value: "monthly", label: "Mensual" },
                  { value: "quarterly", label: "Trimestral" },
                  { value: "semi_annual", label: "Semestral" },
                  { value: "annual", label: "Anual" },
                ]}
              />
            </Field>
            <Field label="Depósito">
              <Input
                value={form.deposit}
                onChange={(v) =>
                  setForm((f) => ({ ...f, deposit: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
          </div>
          {/* Rent history display */}
          {form.rent_history && form.rent_history.length > 0 && (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                background: `${colors.bg}80`,
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 8,
                }}
              >
                Historial de Rentas
              </div>
              {form.rent_history.map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                    fontSize: 12,
                    borderBottom:
                      i < form.rent_history.length - 1
                        ? `1px solid ${colors.border}`
                        : "none",
                  }}
                >
                  <span style={{ color: colors.textMuted }}>
                    {fmtDate(r.date)}
                  </span>
                  <span style={{ color: colors.text, fontWeight: 600 }}>
                    {$fmt(r.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <button
              onClick={() => setModal(null)}
              style={{ ...S.btn, ...S.btnGhost }}
            >
              Cancelar
            </button>
            {form.id && (
              <button
                onClick={() => {
                  const newRent = prompt("Nueva renta:", form.monthly_rent);
                  if (newRent === null) return;
                  const parsed = parseFloat(newRent);
                  if (!parsed || parsed <= 0) {
                    showToast("Monto inválido", "error");
                    return;
                  }
                  const history = [
                    ...(form.rent_history || []),
                    {
                      date: new Date().toISOString().slice(0, 10),
                      amount: form.monthly_rent,
                    },
                  ];
                  const updated = {
                    ...form,
                    monthly_rent: parsed,
                    rent_history: history,
                  };
                  saveContract(updated);
                  showToast(
                    `Renta actualizada: ${$fmt(form.monthly_rent)} → ${$fmt(
                      parsed
                    )}`
                  );
                }}
                style={{
                  ...S.btn,
                  background: `${colors.primary}15`,
                  border: `1px solid ${colors.primary}40`,
                  color: colors.primaryHover,
                }}
              >
                $ Actualizar Renta
              </button>
            )}
            {form.id && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "¿Renovar contrato? El actual se marcará como expirado y se creará uno nuevo."
                    )
                  )
                    return;
                  const oldId = form.id;
                  // Expire current contract
                  await sb
                    .from("contracts")
                    .update({ status: "expired" })
                    .eq("id", oldId);
                  // Calculate new dates: start = old end_date + 1 day, same duration
                  const oldStart = new Date(form.start_date + "T12:00:00");
                  const oldEnd = new Date(form.end_date + "T12:00:00");
                  const durationMs = oldEnd - oldStart;
                  const newStart = new Date(oldEnd.getTime() + 86400000);
                  const newEnd = new Date(newStart.getTime() + durationMs);
                  const toISO = (d) => d.toISOString().slice(0, 10);
                  // Open new contract form pre-filled
                  setModal({
                    type: "contract",
                    data: {
                      tenant_id: form.tenant_id,
                      unit_id: form.unit_id,
                      start_date: toISO(newStart),
                      end_date: toISO(newEnd),
                      monthly_rent: form.monthly_rent,
                      payment_frequency: form.payment_frequency || "monthly",
                      deposit: form.deposit,
                    },
                  });
                  await loadData();
                  showToast(
                    "Contrato anterior expirado. Edita y guarda el nuevo."
                  );
                }}
                style={{
                  ...S.btn,
                  background: colors.yellowBg,
                  border: `1px solid ${colors.yellowBorder}`,
                  color: colors.yellow,
                }}
              >
                ↻ Renovar
              </button>
            )}
            <button
              onClick={() => saveContract(form)}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "payment") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      const activeContracts = contracts.filter((c) => c.status === "active");
      return (
        <Modal
          open
          title={form.id ? "Editar Pago" : "Registrar Pago"}
          onClose={() => setModal(null)}
        >
          {!form.contract_id && (
            <Field label="Contrato (Inquilino — Unidad)">
              <Select
                value={form.contract_id}
                onChange={(v) => {
                  const c = contracts.find((c) => c.id === v);
                  const t = tenants.find((t) => t.id === c?.tenant_id);
                  setForm((f) => ({
                    ...f,
                    contract_id: v,
                    tenant_id: c?.tenant_id,
                    unit_id: c?.unit_id,
                    amount: c?.monthly_rent || f.amount,
                  }));
                }}
                options={activeContracts.map((c) => {
                  const t = tenants.find((t) => t.id === c.tenant_id);
                  const u = units.find((u) => u.id === c.unit_id);
                  return {
                    value: c.id,
                    label: `${t?.name || "?"} — ${u?.code || "?"} (${$fmt(
                      c.monthly_rent
                    )})`,
                  };
                })}
                placeholder="Seleccionar"
              />
            </Field>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Periodo (YYYY-MM)">
              <Input
                value={form.period}
                onChange={(v) => setForm((f) => ({ ...f, period: v }))}
                placeholder="2026-02"
              />
            </Field>
            <Field label="Monto">
              <Input
                value={form.amount}
                onChange={(v) =>
                  setForm((f) => ({ ...f, amount: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
            <Field label="Fecha límite">
              <Input
                value={form.due_date}
                onChange={(v) => setForm((f) => ({ ...f, due_date: v }))}
                type="date"
              />
            </Field>
            <Field label="Fecha de pago">
              <Input
                value={form.payment_date}
                onChange={(v) => setForm((f) => ({ ...f, payment_date: v }))}
                type="date"
              />
            </Field>
            <Field label="Método">
              <Select
                value={form.payment_method}
                onChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}
                options={[
                  { value: "transferencia", label: "Transferencia" },
                  { value: "efectivo", label: "Efectivo" },
                  { value: "cheque", label: "Cheque" },
                  { value: "deposito", label: "Depósito" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Estado">
              <Select
                value={form.status}
                onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                options={[
                  { value: "paid", label: "Pagado" },
                  { value: "pending", label: "Pendiente" },
                  { value: "overdue", label: "Vencido" },
                  { value: "partial", label: "Parcial" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
          </div>
          <Field label="Referencia">
            <Input
              value={form.reference}
              onChange={(v) => setForm((f) => ({ ...f, reference: v }))}
              placeholder="Referencia bancaria"
            />
          </Field>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            {form.id && isAdmin && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "¿Eliminar este pago? Esta acción no se puede deshacer."
                    )
                  )
                    return;
                  await sb.from("payments").delete().eq("id", form.id);
                  await loadData();
                  showToast("Pago eliminado");
                  setModal(null);
                }}
                style={{ ...S.btn, ...S.btnDanger, marginRight: "auto" }}
              >
                Eliminar
              </button>
            )}
            <button
              onClick={() => setModal(null)}
              style={{ ...S.btn, ...S.btnGhost }}
            >
              Cancelar
            </button>
            <button
              onClick={() => savePayment(form)}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "maintenance") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      return (
        <Modal
          open
          title={form.id ? "Editar Gasto" : "Nuevo Gasto"}
          onClose={() => setModal(null)}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Categoría">
              <Select
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                options={[
                  "Plomería",
                  "Electricidad",
                  "Elevador",
                  "HVAC",
                  "Pintura",
                  "Limpieza",
                  "Seguridad",
                  "Distribución de Utilidades",
                  "Impuestos",
                  "Contabilidad",
                  "Administración",
                  "Estacionamiento",
                  "Oxtopulco",
                  "Mantenimiento",
                  "Otro",
                ].map((c) => ({ value: c, label: c }))}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Unidad (vacío = área común)">
              <Select
                value={form.unit_id || ""}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    unit_id: v || null,
                    is_common_area: !v,
                  }))
                }
                options={[
                  { value: "", label: "Área Común" },
                  ...units.map((u) => ({ value: u.id, label: u.code })),
                ]}
              />
            </Field>
          </div>
          <Field label="Descripción">
            <Input
              value={form.description}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))}
              placeholder="Descripción del trabajo"
            />
          </Field>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Proveedor">
              <Input
                value={form.vendor}
                onChange={(v) => setForm((f) => ({ ...f, vendor: v }))}
              />
            </Field>
            <Field label="Costo">
              <Input
                value={form.cost}
                onChange={(v) =>
                  setForm((f) => ({ ...f, cost: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
            <Field label="Fecha programada">
              <Input
                value={form.scheduled_date}
                onChange={(v) => setForm((f) => ({ ...f, scheduled_date: v }))}
                type="date"
              />
            </Field>
            <Field label="Estado">
              <Select
                value={form.status}
                onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                options={[
                  { value: "scheduled", label: "Programado" },
                  { value: "completed", label: "Completado" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            {form.id && isAdmin ? (
              <button
                onClick={async () => {
                  if (!window.confirm("¿Eliminar este gasto?")) return;
                  await sb.from("maintenance").delete().eq("id", form.id);
                  await loadData();
                  showToast("Gasto eliminado");
                  setModal(null);
                }}
                style={{ ...S.btn, ...S.btnDanger }}
              >
                Eliminar
              </button>
            ) : (
              <div />
            )}
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setModal(null)}
                style={{ ...S.btn, ...S.btnGhost }}
              >
                Cancelar
              </button>
              <button
                onClick={() => saveMaintenance(form)}
                style={{ ...S.btn, ...S.btnPrimary }}
              >
                Guardar
              </button>
            </div>
          </div>{" "}
        </Modal>
      );
    }

    if (modal.type === "workorder") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      return (
        <Modal
          open
          title={form.id ? "Editar Trabajo" : "Nuevo Trabajo"}
          onClose={() => setModal(null)}
        >
          <Field label="Descripción del trabajo">
            <Input
              value={form.description}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))}
              placeholder="¿Qué se necesita hacer?"
            />
          </Field>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Categoría">
              <Select
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                options={[
                  "Plomería",
                  "Electricidad",
                  "Elevador",
                  "HVAC",
                  "Pintura",
                  "Limpieza",
                  "Cerrajería",
                  "Seguridad",
                  "Albañilería",
                  "Herrería",
                  "Otro",
                ].map((c) => ({ value: c, label: c }))}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Unidad (vacío = área común)">
              <Select
                value={form.unit_id || ""}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    unit_id: v || null,
                    is_common_area: !v,
                  }))
                }
                options={[
                  { value: "", label: "Área Común" },
                  ...units.map((u) => ({ value: u.id, label: u.code })),
                ]}
              />
            </Field>
            <Field label="Prioridad">
              <Select
                value={form.priority}
                onChange={(v) => setForm((f) => ({ ...f, priority: v }))}
                options={[
                  { value: "alta", label: "🔴 Alta" },
                  { value: "media", label: "🟡 Media" },
                  { value: "baja", label: "🟢 Baja" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Fecha de solicitud">
              <Input
                value={form.requested_date}
                onChange={(v) => setForm((f) => ({ ...f, requested_date: v }))}
                type="date"
              />
            </Field>
          </div>
          {form.id && (
            <Field label="Notas del trabajo">
              <Input
                value={form.notes}
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                placeholder="Notas o comentarios"
              />
            </Field>
          )}
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            {form.id && isAdmin ? (
              <button
                onClick={async () => {
                  if (!window.confirm("¿Eliminar este trabajo?")) return;
                  await sb.from("workorders").delete().eq("id", form.id);
                  await loadData();
                  showToast("Trabajo eliminado");
                  setModal(null);
                }}
                style={{ ...S.btn, ...S.btnDanger }}
              >
                Eliminar
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={() =>
                saveWorkorder({
                  ...form,
                  status: form.status || "pending",
                  requested_date:
                    form.requested_date ||
                    new Date().toISOString().slice(0, 10),
                })
              }
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "complete_workorder") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      return (
        <Modal
          open
          title="Marcar como Realizado"
          onClose={() => setModal(null)}
        >
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.text,
                marginBottom: 4,
              }}
            >
              {form.description}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              <span
                style={{
                  background: `${colors.primary}15`,
                  color: colors.primary,
                  padding: "2px 8px",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
              >
                {form.category}
              </span>
            </div>
          </div>
          <Field label="Notas (opcional)">
            <textarea
              value={form.completion_notes || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, completion_notes: e.target.value }))
              }
              placeholder="¿Qué se hizo? Materiales usados, observaciones..."
              rows={3}
              style={{
                ...S.input,
                width: "100%",
                boxSizing: "border-box",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
          </Field>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <button
              onClick={() => setModal(null)}
              style={{ ...S.btn, border: `1px solid ${colors.border}` }}
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                const today = new Date().toISOString().slice(0, 10);
                const notes = form.completion_notes || "";
                await sb
                  .from("workorders")
                  .update({
                    status: "completed",
                    completed_date: today,
                    notes,
                  })
                  .eq("id", form.id);
                await loadData();
                showToast("Trabajo marcado como completado ✓");
                setModal(null);
              }}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              ✓ Confirmar Realizado
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "catastral") {
      const [form, setForm] = [
        modal.data || { catastral: catastralValue },
        (fn) =>
          setModal((m) => ({
            ...m,
            data:
              typeof fn === "function"
                ? fn(m.data || { catastral: catastralValue })
                : fn,
          })),
      ];
      return (
        <Modal
          open
          title="Valor Catastral del Edificio"
          onClose={() => setModal(null)}
          width={400}
        >
          <Field label="Valor catastral">
            <Input
              value={form.catastral}
              onChange={(v) =>
                setForm((f) => ({ ...f, catastral: parseFloat(v) || 0 }))
              }
              type="number"
            />
          </Field>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>
            Rendimiento = (Renta mensual × 12) ÷ (Valor catastral × 2)
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <button
              onClick={() => {
                setCatastralValue(form.catastral || 0);
                showToast("Valor catastral actualizado");
                setModal(null);
              }}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "project") {
      const [form, setForm] = [
        modal.data || {},
        (fn) =>
          setModal((m) => ({
            ...m,
            data: typeof fn === "function" ? fn(m.data) : fn,
          })),
      ];
      return (
        <Modal
          open
          title={form.id ? "Editar Proyecto" : "Nuevo Proyecto"}
          onClose={() => setModal(null)}
        >
          <Field label="Nombre del proyecto">
            <Input
              value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              placeholder="Ej: Acceso discapacitados"
            />
          </Field>
          <Field label="Descripción">
            <Input
              value={form.description}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))}
              placeholder="Descripción del proyecto"
            />
          </Field>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            <Field label="Prioridad">
              <Select
                value={form.priority}
                onChange={(v) => setForm((f) => ({ ...f, priority: v }))}
                options={[
                  { value: "alta", label: "🔴 Alta" },
                  { value: "media", label: "🟡 Media" },
                  { value: "baja", label: "🟢 Baja" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                options={[
                  { value: "planeado", label: "Planeado" },
                  { value: "en_progreso", label: "En Progreso" },
                  { value: "completado", label: "Completado" },
                ]}
                placeholder="Seleccionar"
              />
            </Field>
            <Field label="Fecha estimada de inicio">
              <Input
                value={form.estimated_start}
                onChange={(v) => setForm((f) => ({ ...f, estimated_start: v }))}
                type="date"
              />
            </Field>
            <Field label="Costo estimado">
              <Input
                value={form.estimated_cost}
                onChange={(v) =>
                  setForm((f) => ({ ...f, estimated_cost: parseFloat(v) || 0 }))
                }
                type="number"
              />
            </Field>
          </div>
          <Field label="Notas">
            <Input
              value={form.notes}
              onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
              placeholder="Notas adicionales"
            />
          </Field>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            {form.id && isAdmin ? (
              <button
                onClick={async () => {
                  if (!window.confirm("¿Eliminar este proyecto?")) return;
                  await sb.from("projects").delete().eq("id", form.id);
                  await loadData();
                  showToast("Proyecto eliminado");
                  setModal(null);
                }}
                style={{ ...S.btn, ...S.btnDanger }}
              >
                Eliminar
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={() =>
                saveProject({
                  ...form,
                  status: form.status || "planeado",
                })
              }
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Guardar
            </button>
          </div>
        </Modal>
      );
    }

    if (modal.type === "overdue_list") {
      return (
        <Modal
          open
          title="Todos los Adeudos"
          onClose={() => setModal(null)}
          width={700}
        >
          {overduePayments.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 20,
                color: colors.textDim,
                fontSize: 13,
              }}
            >
              ✓ Sin adeudos
            </div>
          ) : (
            <>
              <div
                style={{
                  marginBottom: 14,
                  fontSize: 13,
                  color: colors.textMuted,
                }}
              >
                Total:{" "}
                <strong style={{ color: colors.red }}>
                  {$fmt(totalOverdue)}
                </strong>{" "}
                en {overduePayments.length} pago
                {overduePayments.length !== 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {overduePayments
                  .sort((a, b) =>
                    (a.period || "").localeCompare(b.period || "")
                  )
                  .map((p) => {
                    const u = units.find((u) => u.id === p.unit_id);
                    const t = tenants.find((t) => t.id === p.tenant_id);
                    return (
                      <div
                        key={p.id}
                        onClick={() =>
                          isAdmin &&
                          setModal({ type: "payment", data: { ...p } })
                        }
                        style={{
                          display: isMobile ? "flex" : "grid",
                          flexDirection: isMobile ? "column" : undefined,
                          gridTemplateColumns: isMobile
                            ? undefined
                            : "70px 1fr 80px 100px 90px",
                          alignItems: isMobile ? "flex-start" : "center",
                          gap: isMobile ? 4 : 10,
                          padding: isMobile ? "10px 12px" : "10px 14px",
                          background: colors.redBg,
                          border: `1px solid ${colors.redBorder}`,
                          borderRadius: 8,
                          cursor: isAdmin ? "pointer" : "default",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: colors.red,
                          }}
                        >
                          {u?.code}
                        </span>
                        <span style={{ fontSize: 13 }}>{t?.name}</span>
                        <span style={{ fontSize: 12, color: colors.textMuted }}>
                          {p.period}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            textAlign: "right",
                          }}
                        >
                          {$fmt(p.amount)}
                        </span>
                        <Badge type="overdue" text="Vencido" />
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </Modal>
      );
    }

    return null;
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.bg,
        color: colors.text,
        fontFamily: "'Outfit', sans-serif",
        transition: "background 0.3s ease, color 0.3s ease",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 2000,
            background:
              toast.type === "success" ? colors.greenBg : colors.redBg,
            border: `1px solid ${
              toast.type === "success" ? colors.greenBorder : colors.redBorder
            }`,
            color: toast.type === "success" ? colors.green : colors.red,
            padding: "10px 20px",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            animation: "slideIn 0.3s ease",
          }}
        >
          {toast.type === "success" ? "✓" : "✕"} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div
        style={{
          background: colors.headerBg,
          borderBottom: `1px solid ${colors.border}`,
          padding: isMobile ? "10px 14px" : isDark ? "16px 28px" : "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 8 : 12,
            minWidth: 0,
          }}
        >
          {!isMobile && (
            <img
              src="/TA.png"
              alt="TA"
              style={{ height: 38, objectFit: "contain" }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: isMobile ? 15 : 20,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {isMobile ? "Univ. 1821" : "Av. Universidad 1821"}
            </h1>
            {!isMobile && (
              <span style={{ fontSize: 14, color: colors.textMuted }}>
                {userRole === "maintenance"
                  ? "Panel de Mantenimiento"
                  : userRole === "readonly"
                  ? "Dashboard (Solo lectura)"
                  : "Dashboard de Administración"}
              </span>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 8 : 12,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            {!isMobile && (
              <span style={{ fontSize: 12, color: colors.textMuted }}>
                {sb?.user?.email || authEmail}
              </span>
            )}
            <button
              onClick={() => {
                sb.signOut();
                setIsLoggedIn(false);
                setUserRole("admin");
                setActiveTab("overview");
              }}
              style={{
                background: "transparent",
                border: "none",
                color: colors.red,
                fontSize: 11,
                cursor: "pointer",
                fontFamily: "'Outfit', sans-serif",
                padding: 0,
              }}
            >
              {isMobile ? "Salir" : "Cerrar sesión"}
            </button>
          </div>
          {/* Privacy Toggle */}
          <button
            onClick={() => setPrivacyMode(!privacyMode)}
            style={{
              background: privacyMode ? `${colors.primary}20` : "transparent",
              border: `1px solid ${
                privacyMode ? colors.primary : colors.border
              }`,
              borderRadius: 20,
              padding: isMobile ? "4px 10px" : "6px 14px",
              height: isMobile ? 32 : 38,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontSize: isMobile ? 11 : 12,
              fontWeight: 600,
              fontFamily: "'Outfit', sans-serif",
              color: privacyMode ? colors.primary : colors.textMuted,
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            title={privacyMode ? "Mostrar montos" : "Ocultar montos"}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            {!isMobile && "Privacy"}
          </button>
          <button
            onClick={() => setIsDark(!isDark)}
            style={{
              background: "transparent",
              border: `1px solid ${colors.border}`,
              borderRadius: 20,
              width: isMobile ? 32 : 38,
              height: isMobile ? 32 : 38,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: isMobile ? 14 : 16,
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
            title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            {isDark ? "🌙" : "☀️"}
          </button>
        </div>
      </div>

      <div
        style={{
          padding: isMobile ? "16px 12px" : "20px 28px",
          maxWidth: 1300,
          margin: "0 auto",
        }}
      >
        {/* KPIs */}
        {userRole !== "maintenance" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
              gap: isMobile ? 8 : 14,
              marginBottom: isMobile ? 16 : 24,
            }}
          >
            <KPI
              isDark={isDark}
              icon="📊"
              label="Ocupación"
              value={`${
                units.length
                  ? Math.round((occupiedCount / units.length) * 100)
                  : 0
              }%`}
              sub={`${occupiedCount} de ${units.length} unidades`}
              color={colors.primary}
            />
            <div
              onClick={() => isAdmin && setModal({ type: "catastral" })}
              style={{ cursor: isAdmin ? "pointer" : "default" }}
            >
              <KPI
                isDark={isDark}
                icon="💲"
                label="Renta Mensual"
                value={$fmt(totalMonthlyRent)}
                sub={
                  <span>
                    Rend. anual:{" "}
                    {privacyMode
                      ? "••••%"
                      : `${
                          catastralValue > 0
                            ? (
                                ((totalMonthlyRent * 12) /
                                  (catastralValue * 2)) *
                                100
                              ).toFixed(2)
                            : 0
                        }%`}
                    {isAdmin && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          color: colors.primary,
                        }}
                      >
                        ✎
                      </span>
                    )}
                  </span>
                }
                color={colors.green}
              />
            </div>
            <KPI
              isDark={isDark}
              icon="⚠️"
              label="Adeudos"
              value={$fmt(totalOverdue)}
              sub={
                <span>
                  {overduePayments.length} pagos pendientes
                  {overduePayments.length > 0 && (
                    <span
                      onClick={() => setModal({ type: "overdue_list" })}
                      style={{
                        display: "block",
                        cursor: "pointer",
                        color: colors.primary,
                        textDecoration: "underline",
                        marginTop: 4,
                      }}
                    >
                      Ver adeudos →
                    </span>
                  )}
                </span>
              }
              color={colors.red}
            />
            <KPI
              isDark={isDark}
              icon="🔧"
              label="Gastos del Mes"
              value={$fmt(
                maintenance
                  .filter(
                    (m) =>
                      (m.scheduled_date || m.completed_date || "").slice(
                        0,
                        7
                      ) === new Date().toISOString().slice(0, 7)
                  )
                  .filter((m) => m.status !== "scheduled")
                  .reduce((s, m) => s + m.cost, 0)
              )}
              sub={
                <span
                  onClick={() => {
                    setActiveTab("maintenance");
                    setSelectedUnit(null);
                  }}
                  style={{
                    cursor: "pointer",
                    color: colors.primary,
                    textDecoration: "underline",
                  }}
                >
                  Ver todos los gastos →
                </span>
              }
              color={colors.yellow}
            />
          </div>
        )}

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: isDark ? 6 : 2,
            marginBottom: isMobile ? 14 : 20,
            flexWrap: isMobile ? "nowrap" : "wrap",
            overflowX: isMobile ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
            alignItems: "center",
            borderBottom: isDark ? "none" : `1px solid ${colors.border}`,
            paddingBottom: isDark ? 0 : 0,
          }}
        >
          {userRole !== "maintenance" && (
            <>
              <Tab
                isDark={isDark}
                active={activeTab === "overview"}
                label="Vista General"
                onClick={() => {
                  setActiveTab("overview");
                  setSelectedUnit(null);
                }}
              />
              <Tab
                isDark={isDark}
                active={activeTab === "units"}
                label="Unidades"
                onClick={() => {
                  setActiveTab("units");
                  setSelectedUnit(null);
                }}
                badge={units.length}
              />
              <Tab
                isDark={isDark}
                active={activeTab === "payments"}
                label="Pagos"
                onClick={() => {
                  setActiveTab("payments");
                  setSelectedUnit(null);
                }}
              />
              <Tab
                isDark={isDark}
                active={activeTab === "contracts"}
                label="Contratos"
                onClick={() => {
                  setActiveTab("contracts");
                  setSelectedUnit(null);
                }}
                badge={
                  expiringContracts.length > 0
                    ? isDark
                      ? `⚠ ${expiringContracts.length}`
                      : `${expiringContracts.length}`
                    : null
                }
              />
              <Tab
                isDark={isDark}
                active={activeTab === "maintenance"}
                label="Gastos"
                onClick={() => {
                  setActiveTab("maintenance");
                  setSelectedUnit(null);
                }}
              />
            </>
          )}
          <Tab
            isDark={isDark}
            active={activeTab === "workorders"}
            label="Trabajos"
            onClick={() => {
              setActiveTab("workorders");
              setSelectedUnit(null);
            }}
            badge={
              workorders.filter((w) => w.status === "pending").length || null
            }
          />
          {userRole !== "maintenance" && (
            <Tab
              isDark={isDark}
              active={activeTab === "projects"}
              label="Proyectos"
              onClick={() => {
                setActiveTab("projects");
                setSelectedUnit(null);
              }}
              badge={
                projects.filter((p) => p.status !== "completado").length || null
              }
            />
          )}
          <div style={{ flex: 1 }} />
          {isAdmin && activeTab !== "overview" && (
            <button
              onClick={() => {
                const types = {
                  units: "unit",
                  payments: "payment",
                  contracts: "contract",
                  maintenance: "maintenance",
                  workorders: "workorder",
                  projects: "project",
                };
                setModal({ type: types[activeTab] || "unit", data: {} });
              }}
              style={{ ...S.btn, ...S.btnPrimary, padding: "8px 16px" }}
            >
              + Agregar
            </button>
          )}
        </div>

        {/* ═══════════ OVERVIEW ═══════════ */}
        {activeTab === "overview" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 12 : 16,
            }}
          >
            {/* Building Map */}
            <div style={{ ...S.card, gridColumn: "1 / -1" }}>
              <h3
                style={{
                  margin: "0 0 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.textMuted,
                }}
              >
                Mapa del Edificio
              </h3>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {["3", "2", "1", "PB"].map((floor) => {
                  const floorUnits = enrichedUnits.filter(
                    (u) => u.floor === floor
                  );
                  if (!floorUnits.length) return null;
                  return (
                    <div
                      key={floor}
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <div
                        style={{
                          width: 44,
                          textAlign: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          color: colors.textDim,
                          background: `${colors.primary}10`,
                          padding: "6px 0",
                          borderRadius: 6,
                        }}
                      >
                        {floor === "PB" ? "PB" : `P${floor}`}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flex: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {floorUnits.map((u) => {
                          const statusMap = {
                            active: colors.green,
                            expiring: colors.yellow,
                            expired: colors.red,
                            vacant: colors.purple,
                          };
                          const c = statusMap[u.status];
                          return (
                            <div
                              key={u.id}
                              onClick={() => {
                                setSelectedUnit(u);
                                setActiveTab("units");
                              }}
                              style={{
                                flex: `1 1 ${isMobile ? 90 : u.sqm * 1.3}px`,
                                minWidth: isMobile ? 90 : 110,
                                background: `${c}10`,
                                border: `1px solid ${c}30`,
                                borderRadius: 10,
                                padding: isMobile ? "8px 10px" : "10px 12px",
                                cursor: "pointer",
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.borderColor = `${c}70`)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.borderColor = `${c}30`)
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 3,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: c,
                                  }}
                                >
                                  {u.code}{" "}
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 400,
                                      opacity: 0.7,
                                    }}
                                  >
                                    (
                                    {totalMonthlyRent > 0
                                      ? (
                                          ((u.contract?.monthly_rent ||
                                            u.base_rent) /
                                            totalMonthlyRent) *
                                          100
                                        ).toFixed(1)
                                      : 0}
                                    %)
                                  </span>
                                </span>
                                <Badge
                                  type={u.status}
                                  text={
                                    {
                                      active: "Vigente",
                                      expiring: "Por vencer",
                                      expired: "Vencido",
                                      vacant: "Vacante",
                                    }[u.status]
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: colors.text,
                                  fontWeight: 500,
                                }}
                              >
                                {u.tenant?.name || "Disponible"}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: colors.textMuted,
                                  marginTop: 2,
                                }}
                              >
                                {u.sqm}m² ·{" "}
                                {$fmt(u.contract?.monthly_rent || u.base_rent)}
                                /mes
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Alerts */}
            <div style={S.card}>
              <h3
                style={{
                  margin: "0 0 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.textMuted,
                }}
              >
                {isDark ? "⚡ " : ""}Alertas Activas
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {overduePayments.map((p) => {
                  const t = tenants.find((t) => t.id === p.tenant_id);
                  const u = units.find((u) => u.id === p.unit_id);
                  return (
                    <div
                      key={p.id}
                      style={{
                        background: colors.redBg,
                        border: `1px solid ${colors.redBorder}`,
                        borderRadius: 10,
                        padding: "10px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: colors.red,
                          }}
                        >
                          Pago vencido — {u?.code}
                        </div>
                        <div style={{ fontSize: 11, color: colors.textMuted }}>
                          {t?.name} · {p.period}
                        </div>
                      </div>
                      <span
                        style={{
                          fontWeight: 700,
                          color: colors.red,
                          fontSize: 13,
                        }}
                      >
                        {$fmt(p.amount)}
                      </span>
                    </div>
                  );
                })}
                {expiringContracts.map((u) => (
                  <div
                    key={u.id}
                    style={{
                      background: colors.yellowBg,
                      border: `1px solid ${colors.yellowBorder}`,
                      borderRadius: 10,
                      padding: "10px 14px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.yellow,
                        }}
                      >
                        Contrato por vencer — {u.code}
                      </div>
                      <div style={{ fontSize: 11, color: "#8a7a3a" }}>
                        {u.tenant?.name} · Vence:{" "}
                        {fmtDate(u.contract?.end_date)}
                      </div>
                    </div>
                    <span
                      style={{
                        fontWeight: 700,
                        color: colors.yellow,
                        fontSize: 13,
                      }}
                    >
                      {u.daysLeft}d
                    </span>
                  </div>
                ))}
                {overduePayments.length === 0 &&
                  expiringContracts.length === 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: 20,
                        color: colors.textDim,
                        fontSize: 13,
                      }}
                    >
                      ✓ Sin alertas activas
                    </div>
                  )}
              </div>
            </div>

            {/* Recent payments */}
            <div style={S.card}>
              <h3
                style={{
                  margin: "0 0 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.textMuted,
                }}
              >
                Pagos Recientes
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {payments
                  .filter((p) => p.status === "paid")
                  .sort((a, b) =>
                    (b.payment_date || "").localeCompare(a.payment_date || "")
                  )
                  .slice(0, 8)
                  .map((p) => {
                    const u = units.find((u) => u.id === p.unit_id);
                    const t = tenants.find((t) => t.id === p.tenant_id);
                    return (
                      <div
                        key={p.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 12px",
                          background: `${colors.green}08`,
                          borderRadius: 8,
                        }}
                      >
                        <div>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: colors.text,
                            }}
                          >
                            {u?.code}{" "}
                          </span>
                          <span
                            style={{ fontSize: 12, color: colors.textMuted }}
                          >
                            {t?.name}
                          </span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: colors.green,
                            }}
                          >
                            {$fmt(p.amount)}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: colors.textDim,
                              marginLeft: 8,
                            }}
                          >
                            {fmtDate(p.payment_date)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ UNITS / TENANTS TAB ═══════════ */}
        {activeTab === "units" && !selectedUnit && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {enrichedUnits.map((u) => (
              <div
                key={u.id}
                onClick={() => setSelectedUnit(u)}
                style={{
                  ...S.card,
                  padding: isMobile ? "12px 14px" : "14px 20px",
                  cursor: "pointer",
                  display: isMobile ? "flex" : "grid",
                  flexDirection: isMobile ? "column" : undefined,
                  gridTemplateColumns: isMobile
                    ? undefined
                    : "70px 1fr 130px 110px 100px 28px",
                  alignItems: isMobile ? undefined : "center",
                  gap: isMobile ? 6 : 14,
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = colors.borderFocus)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = colors.border)
                }
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: {
                      active: colors.green,
                      expiring: colors.yellow,
                      expired: colors.red,
                      vacant: colors.purple,
                    }[u.status],
                  }}
                >
                  {u.code}
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {u.tenant?.name || "— Vacante —"}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textMuted }}>
                    {u.type} · {u.sqm}m² · Piso {u.floor}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {$fmt(u.contract?.monthly_rent || u.base_rent)}
                  </div>
                  <div style={{ fontSize: 10, color: colors.textMuted }}>
                    {
                      {
                        monthly: "mensual",
                        quarterly: "trimestral",
                        semi_annual: "semestral",
                        annual: "anual",
                      }[u.contract?.payment_frequency || "monthly"]
                    }
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {u.paymentSt ? (
                    <Badge type={u.paymentSt.status} text={u.paymentSt.label} />
                  ) : (
                    <span style={{ fontSize: 11, color: colors.textDim }}>
                      —
                    </span>
                  )}
                </div>
                <Badge
                  type={u.status}
                  text={
                    {
                      active: "Vigente",
                      expiring: "Por vencer",
                      expired: "Vencido",
                      vacant: "Vacante",
                    }[u.status]
                  }
                />
                <span style={{ color: colors.textDim }}>›</span>
              </div>
            ))}
          </div>
        )}

        {/* Unit Detail */}
        {activeTab === "units" &&
          selectedUnit &&
          (() => {
            const u =
              enrichedUnits.find((eu) => eu.id === selectedUnit.id) ||
              selectedUnit;
            return (
              <div>
                <button
                  onClick={() => setSelectedUnit(null)}
                  style={{
                    background: "none",
                    border: "none",
                    color: colors.primary,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 14,
                    padding: 0,
                    fontFamily: "inherit",
                  }}
                >
                  ← Volver
                </button>
                <div style={S.card}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 20,
                      flexWrap: "wrap",
                      gap: 14,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: {
                              active: colors.green,
                              expiring: colors.yellow,
                              expired: colors.red,
                              vacant: colors.purple,
                            }[u.status],
                          }}
                        >
                          {u.code}
                        </span>
                        <Badge
                          type={u.status}
                          text={
                            {
                              active: "Vigente",
                              expiring: "Por vencer",
                              expired: "Vencido",
                              vacant: "Vacante",
                            }[u.status]
                          }
                        />
                      </div>
                      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                        {u.tenant?.name || "Unidad Vacante"}
                      </h2>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {isAdmin && u.tenant && (
                        <button
                          onClick={() =>
                            setModal({ type: "tenant", data: { ...u.tenant } })
                          }
                          style={{
                            ...S.btn,
                            ...S.btnGhost,
                            padding: "6px 14px",
                            fontSize: 12,
                          }}
                        >
                          ✎ Editar Inquilino
                        </button>
                      )}
                      {isAdmin && u.tenant && (
                        <button
                          onClick={async () => {
                            if (
                              !window.confirm(
                                `¿Desocupar ${u.code}? El inquilino "${u.tenant.name}" se marcará como inactivo.`
                              )
                            )
                              return;
                            await sb
                              .from("tenants")
                              .update({ status: "inactive" })
                              .eq("id", u.tenant.id);
                            if (u.contract)
                              await sb
                                .from("contracts")
                                .update({ status: "expired" })
                                .eq("id", u.contract.id);
                            await loadData();
                            showToast("Unidad desocupada");
                            setSelectedUnit(null);
                          }}
                          style={{
                            ...S.btn,
                            ...S.btnDanger,
                            padding: "6px 14px",
                            fontSize: 12,
                          }}
                        >
                          ✕ Desocupar
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() =>
                            setModal({
                              type: "unit",
                              data: {
                                ...u,
                                tenant: undefined,
                                contract: undefined,
                                payments: undefined,
                                status: undefined,
                                daysLeft: undefined,
                              },
                            })
                          }
                          style={{
                            ...S.btn,
                            ...S.btnGhost,
                            padding: "6px 14px",
                            fontSize: 12,
                          }}
                        >
                          ✎ Editar Unidad
                        </button>
                      )}
                      {isAdmin && !u.tenant && (
                        <button
                          onClick={() =>
                            setModal({
                              type: "tenant",
                              data: { unit_id: u.id },
                            })
                          }
                          style={{
                            ...S.btn,
                            ...S.btnPrimary,
                            padding: "6px 14px",
                            fontSize: 12,
                          }}
                        >
                          + Asignar Inquilino
                        </button>
                      )}
                    </div>
                  </div>

                  {u.tenant && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 14,
                        padding: 18,
                        background: `${colors.primary}06`,
                        borderRadius: 12,
                        marginBottom: 20,
                      }}
                    >
                      <div>
                        <div style={S.label}>Contacto</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {u.tenant.contact_name}
                        </div>
                        <div style={{ fontSize: 12, color: colors.primary }}>
                          {u.tenant.email}
                        </div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>
                          {u.tenant.phone}
                        </div>
                      </div>
                      {u.contract && (
                        <>
                          <div>
                            <div style={S.label}>Contrato</div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                              {fmtDate(u.contract.start_date)} →{" "}
                              {fmtDate(u.contract.end_date)}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color:
                                  u.daysLeft <= 60
                                    ? colors.yellow
                                    : colors.textMuted,
                              }}
                            >
                              {u.daysLeft > 0
                                ? `Vence en ${u.daysLeft} días`
                                : "Contrato vencido"}
                            </div>
                          </div>
                          <div>
                            <div style={S.label}>Renta / Depósito</div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                              {$fmt(u.contract.monthly_rent)} / mes
                            </div>
                            <div
                              style={{ fontSize: 12, color: colors.textMuted }}
                            >
                              Depósito: {$fmt(u.contract.deposit)}
                            </div>
                          </div>
                        </>
                      )}
                      <div>
                        <div style={S.label}>Superficie</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {u.sqm} m²
                        </div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>
                          {u.type} · Piso {u.floor}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment history */}
                  {u.payments.length > 0 && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            fontSize: 14,
                            fontWeight: 700,
                            color: colors.textMuted,
                          }}
                        >
                          Historial de Pagos
                        </h3>
                        {isAdmin && (
                          <button
                            onClick={() =>
                              setModal({
                                type: "payment",
                                data: {
                                  contract_id: u.contract?.id,
                                  tenant_id: u.tenant?.id,
                                  unit_id: u.id,
                                  amount: u.contract?.monthly_rent || 0,
                                  status: "paid",
                                },
                              })
                            }
                            style={{
                              ...S.btn,
                              ...S.btnPrimary,
                              padding: "6px 14px",
                              fontSize: 12,
                            }}
                          >
                            + Registrar Pago
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {u.payments.map((p) => (
                          <div
                            key={p.id}
                            onClick={() =>
                              isAdmin &&
                              setModal({ type: "payment", data: { ...p } })
                            }
                            style={{
                              display: isMobile ? "flex" : "grid",
                              flexDirection: isMobile ? "column" : undefined,
                              gridTemplateColumns: isMobile
                                ? undefined
                                : "90px 1fr 100px 90px",
                              alignItems: isMobile ? "flex-start" : "center",
                              gap: isMobile ? 4 : 10,
                              padding: isMobile ? "10px 12px" : "10px 14px",
                              background: `${colors.bg}80`,
                              borderRadius: 8,
                              borderLeft: `3px solid ${
                                {
                                  paid: colors.green,
                                  overdue: colors.red,
                                  pending: colors.yellow,
                                  partial: colors.yellow,
                                }[p.status]
                              }`,
                              cursor: isAdmin ? "pointer" : "default",
                            }}
                          >
                            <span
                              style={{ fontSize: 12, color: colors.textMuted }}
                            >
                              {p.period}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              {$fmt(p.amount)}
                            </span>
                            <span
                              style={{ fontSize: 11, color: colors.textMuted }}
                            >
                              {fmtDate(p.payment_date)}
                            </span>
                            <Badge
                              type={p.status}
                              text={
                                {
                                  paid: "Pagado",
                                  overdue: "Vencido",
                                  pending: "Pendiente",
                                  partial: "Parcial",
                                }[p.status]
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Historial de Ocupantes */}
                  {(() => {
                    const pastTenants = (u.allTenants || []).filter(
                      (t) => t.status === "inactive"
                    );
                    if (pastTenants.length === 0) return null;
                    return (
                      <>
                        <h3
                          style={{
                            margin: "20px 0 12px",
                            fontSize: 14,
                            fontWeight: 700,
                            color: colors.textMuted,
                          }}
                        >
                          Historial de Ocupantes
                        </h3>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          {pastTenants.map((t) => {
                            const tContracts = (u.allContracts || []).filter(
                              (c) => c.tenant_id === t.id
                            );
                            const tPayments = u.payments.filter(
                              (p) => p.tenant_id === t.id
                            );
                            const totalPaid = tPayments
                              .filter((p) => p.status === "paid")
                              .reduce((s, p) => s + p.amount, 0);
                            return (
                              <div
                                key={t.id}
                                style={{
                                  background: `${colors.bg}80`,
                                  borderRadius: 12,
                                  padding: 16,
                                  borderLeft: `3px solid ${colors.textDim}`,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8,
                                  }}
                                >
                                  <div>
                                    <span
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: colors.text,
                                      }}
                                    >
                                      {t.name}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: colors.textMuted,
                                        marginLeft: 8,
                                      }}
                                    >
                                      {t.contact_name}
                                    </span>
                                  </div>
                                  <Badge type="expired" text="Inactivo" />
                                </div>
                                {tContracts.map((c) => (
                                  <div
                                    key={c.id}
                                    style={{
                                      fontSize: 12,
                                      color: colors.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    Contrato: {fmtDate(c.start_date)} →{" "}
                                    {fmtDate(c.end_date)} ·{" "}
                                    {$fmt(c.monthly_rent)}/mes
                                  </div>
                                ))}
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: colors.textMuted,
                                    marginTop: 4,
                                  }}
                                >
                                  {tPayments.length} pagos registrados · Total
                                  cobrado:{" "}
                                  <strong style={{ color: colors.green }}>
                                    {$fmt(totalPaid)}
                                  </strong>
                                </div>
                                {tPayments.length > 0 && (
                                  <details style={{ marginTop: 8 }}>
                                    <summary
                                      style={{
                                        fontSize: 11,
                                        color: colors.primary,
                                        cursor: "pointer",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Ver pagos
                                    </summary>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        marginTop: 6,
                                      }}
                                    >
                                      {tPayments.map((p) => (
                                        <div
                                          key={p.id}
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                              "90px 1fr 100px 80px",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "6px 10px",
                                            background: `${colors.bgInput}60`,
                                            borderRadius: 6,
                                            fontSize: 12,
                                          }}
                                        >
                                          <span
                                            style={{ color: colors.textMuted }}
                                          >
                                            {p.period}
                                          </span>
                                          <span style={{ fontWeight: 600 }}>
                                            {$fmt(p.amount)}
                                          </span>
                                          <span
                                            style={{ color: colors.textMuted }}
                                          >
                                            {fmtDate(p.payment_date)}
                                          </span>
                                          <Badge
                                            type={p.status}
                                            text={
                                              {
                                                paid: "Pagado",
                                                overdue: "Vencido",
                                                pending: "Pendiente",
                                                partial: "Parcial",
                                              }[p.status]
                                            }
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })()}

        {/* ═══════════ PAYMENTS TAB ═══════════ */}
        {activeTab === "payments" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {isAdmin && (
                <button
                  onClick={generateMonthlyPayments}
                  style={{
                    ...S.btn,
                    ...S.btnGhost,
                    padding: "6px 14px",
                    fontSize: 12,
                  }}
                >
                  Generar pagos del mes
                </button>
              )}
              <span
                style={{
                  fontSize: 11,
                  color: colors.textMuted,
                  fontWeight: 600,
                }}
              >
                MES:
              </span>
              <input
                type="month"
                value={paymentMonth}
                onChange={(e) => setPaymentMonth(e.target.value)}
                style={{
                  ...S.input,
                  width: 160,
                  padding: "6px 10px",
                  fontSize: 12,
                }}
              />
            </div>
            {(() => {
              const monthPayments = payments.filter(
                (p) => p.period === paymentMonth
              );
              const paidAmount = monthPayments
                .filter((p) => p.status === "paid")
                .reduce((s, p) => s + p.amount, 0);
              const overdueAmount = monthPayments
                .filter((p) => p.status === "overdue")
                .reduce((s, p) => s + p.amount, 0);
              const pendingCount = monthPayments.filter(
                (p) => p.status === "pending"
              ).length;
              const monthLabel = new Date(
                paymentMonth + "-15"
              ).toLocaleDateString("es-MX", {
                month: "short",
                year: "numeric",
              });
              return (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 14,
                    }}
                  >
                    <div
                      style={{
                        background: colors.greenBg,
                        border: `1px solid ${colors.greenBorder}`,
                        borderRadius: 14,
                        padding: "14px 18px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: colors.green,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 4,
                        }}
                      >
                        Cobrado ({monthLabel})
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: colors.green,
                        }}
                      >
                        {$fmt(paidAmount)}
                      </div>
                    </div>
                    <div
                      style={{
                        background: colors.redBg,
                        border: `1px solid ${colors.redBorder}`,
                        borderRadius: 14,
                        padding: "14px 18px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: colors.red,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 4,
                        }}
                      >
                        Adeudos ({monthLabel})
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: colors.red,
                        }}
                      >
                        {$fmt(overdueAmount)}
                      </div>
                    </div>
                    <div
                      style={{
                        background: colors.yellowBg,
                        border: `1px solid ${colors.yellowBorder}`,
                        borderRadius: 14,
                        padding: "14px 18px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: colors.yellow,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 4,
                        }}
                      >
                        Pendientes ({monthLabel})
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: colors.yellow,
                        }}
                      >
                        {pendingCount}
                      </div>
                    </div>
                  </div>

                  <div style={S.card}>
                    <h3
                      style={{
                        margin: "0 0 14px",
                        fontSize: 14,
                        fontWeight: 700,
                        color: colors.textMuted,
                      }}
                    >
                      Ingresos Mensuales (últimos 12 meses)
                    </h3>
                    {(() => {
                      const months = [];
                      const now = new Date();
                      for (let i = 11; i >= 0; i--) {
                        const d = new Date(
                          now.getFullYear(),
                          now.getMonth() - i,
                          1
                        );
                        const key = `${d.getFullYear()}-${String(
                          d.getMonth() + 1
                        ).padStart(2, "0")}`;
                        const label = d
                          .toLocaleDateString("es-MX", { month: "short" })
                          .replace(".", "");
                        const total = payments
                          .filter(
                            (p) => p.period === key && p.status === "paid"
                          )
                          .reduce((s, p) => s + p.amount, 0);
                        months.push({ key, label, total });
                      }
                      const maxVal = Math.max(...months.map((m) => m.total), 1);
                      const currentKey = `${now.getFullYear()}-${String(
                        now.getMonth() + 1
                      ).padStart(2, "0")}`;
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 6,
                            height: 180,
                          }}
                        >
                          {months.map((m) => (
                            <div
                              key={m.key}
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 9,
                                  color: colors.textMuted,
                                  fontWeight: 600,
                                }}
                              >
                                {m.total > 0
                                  ? privacyMode
                                    ? "$••"
                                    : `$${Math.round(m.total / 1000)}k`
                                  : ""}
                              </span>
                              <div
                                style={{
                                  width: "100%",
                                  maxWidth: 40,
                                  borderRadius: 6,
                                  height: `${Math.max(
                                    (m.total / maxVal) * 140,
                                    4
                                  )}px`,
                                  background:
                                    m.key === currentKey
                                      ? `linear-gradient(180deg, ${colors.primary}, #4338ca)`
                                      : `${colors.border}`,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: 10,
                                  color:
                                    m.key === currentKey
                                      ? colors.primary
                                      : colors.textMuted,
                                  fontWeight: m.key === currentKey ? 700 : 400,
                                }}
                              >
                                {m.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  <div style={S.card}>
                    <h3
                      style={{
                        margin: "0 0 14px",
                        fontSize: 14,
                        fontWeight: 700,
                        color: colors.textMuted,
                      }}
                    >
                      Pagos —{" "}
                      {new Date(paymentMonth + "-15").toLocaleDateString(
                        "es-MX",
                        { month: "long", year: "numeric" }
                      )}
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {monthPayments.length === 0 && (
                        <div
                          style={{
                            color: colors.textMuted,
                            fontSize: 13,
                            textAlign: "center",
                            padding: 20,
                          }}
                        >
                          Sin pagos en este mes
                        </div>
                      )}
                      {monthPayments
                        .sort((a, b) =>
                          (b.payment_date || "z").localeCompare(
                            a.payment_date || "z"
                          )
                        )
                        .map((p) => {
                          const u = units.find((u) => u.id === p.unit_id);
                          const t = tenants.find((t) => t.id === p.tenant_id);
                          return (
                            <div
                              key={p.id}
                              onClick={() =>
                                isAdmin &&
                                setModal({ type: "payment", data: { ...p } })
                              }
                              style={{
                                display: isMobile ? "flex" : "grid",
                                flexDirection: isMobile ? "column" : undefined,
                                gridTemplateColumns: isMobile
                                  ? undefined
                                  : "70px 1fr 100px 100px 90px",
                                alignItems: isMobile ? "flex-start" : "center",
                                gap: isMobile ? 4 : 10,
                                padding: isMobile ? "10px 12px" : "10px 14px",
                                background: `${colors.bg}80`,
                                borderRadius: 8,
                                cursor: isAdmin ? "pointer" : "default",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: isMobile ? "100%" : "auto",
                                  ...(isMobile ? {} : { display: "contents" }),
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: colors.primary,
                                  }}
                                >
                                  {u?.code}
                                </span>
                                {isMobile && (
                                  <Badge
                                    type={p.status}
                                    text={
                                      {
                                        paid: "Pagado",
                                        overdue: "Vencido",
                                        pending: "Pendiente",
                                        partial: "Parcial",
                                      }[p.status]
                                    }
                                  />
                                )}
                              </div>
                              <div>
                                <span style={{ fontSize: 13 }}>{t?.name}</span>
                              </div>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  textAlign: isMobile ? "left" : "right",
                                }}
                              >
                                {$fmt(p.amount)}
                                {isMobile && p.payment_date && (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: colors.textMuted,
                                      fontWeight: 400,
                                      marginLeft: 8,
                                    }}
                                  >
                                    {fmtDate(p.payment_date)}
                                  </span>
                                )}
                              </span>
                              {!isMobile && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: colors.textMuted,
                                    textAlign: "right",
                                  }}
                                >
                                  {fmtDate(p.payment_date)}
                                </span>
                              )}
                              {!isMobile && (
                                <Badge
                                  type={p.status}
                                  text={
                                    {
                                      paid: "Pagado",
                                      overdue: "Vencido",
                                      pending: "Pendiente",
                                      partial: "Parcial",
                                    }[p.status]
                                  }
                                />
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ═══════════ CONTRACTS TAB ═══════════ */}
        {activeTab === "contracts" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {expiringContracts.length > 0 && (
              <div
                style={{
                  background: colors.yellowBg,
                  border: `1px solid ${colors.yellowBorder}`,
                  borderRadius: 16,
                  padding: 20,
                }}
              >
                <h3
                  style={{
                    margin: "0 0 12px",
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.yellow,
                  }}
                >
                  Contratos por Vencer
                </h3>
                {expiringContracts.map((u) => (
                  <div
                    key={u.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 0",
                      borderBottom: `1px solid ${colors.yellowBorder}`,
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: 700,
                          color: colors.yellow,
                          marginRight: 10,
                        }}
                      >
                        {u.code}
                      </span>
                      <span style={{ color: colors.text }}>
                        {u.tenant?.name}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: colors.textMuted,
                          marginLeft: 8,
                        }}
                      >
                        Vence: {fmtDate(u.contract?.end_date)}
                      </span>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <span style={{ fontWeight: 600 }}>
                        {$fmt(u.contract?.monthly_rent)}/mes
                      </span>
                      <span
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: u.daysLeft <= 30 ? colors.red : colors.yellow,
                        }}
                      >
                        {u.daysLeft}d
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={S.card}>
              <h3
                style={{
                  margin: "0 0 6px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.text,
                }}
              >
                Línea de Tiempo de Contratos
              </h3>
              <p
                style={{
                  margin: "0 0 20px",
                  fontSize: 12,
                  color: colors.textMuted,
                }}
              >
                Vista de vigencia de contratos activos. La línea roja indica hoy
                ({fmtDate(new Date().toISOString().slice(0, 10))}).
              </p>
              {(() => {
                const activeC = contracts.filter((c) => c.status === "active");
                if (activeC.length === 0)
                  return (
                    <div
                      style={{
                        color: colors.textMuted,
                        fontSize: 13,
                        textAlign: "center",
                        padding: 20,
                      }}
                    >
                      No hay contratos activos
                    </div>
                  );
                const allDates = activeC.flatMap((c) => [
                  new Date(c.start_date),
                  new Date(c.end_date),
                ]);
                const minDate = new Date(Math.min(...allDates));
                const maxDate = new Date(Math.max(...allDates));
                const padMonths = 3;
                const timeStart = new Date(
                  minDate.getFullYear(),
                  minDate.getMonth() - padMonths,
                  1
                );
                const timeEnd = new Date(
                  maxDate.getFullYear(),
                  maxDate.getMonth() + padMonths + 1,
                  0
                );
                const totalMs = timeEnd - timeStart;
                const toPercent = (d) =>
                  ((new Date(d) - timeStart) / totalMs) * 100;
                const todayPct = toPercent(new Date());
                const years = [];
                for (
                  let y = timeStart.getFullYear();
                  y <= timeEnd.getFullYear();
                  y++
                ) {
                  const pct = toPercent(new Date(y, 0, 1));
                  if (pct >= 0 && pct <= 100) years.push({ year: y, pct });
                }
                return (
                  <div
                    style={{
                      position: "relative",
                      paddingLeft: 80,
                      minHeight: activeC.length * 44 + 40,
                    }}
                  >
                    {/* Year labels */}
                    <div
                      style={{
                        position: "relative",
                        height: 24,
                        marginBottom: 8,
                      }}
                    >
                      {years.map((y) => (
                        <span
                          key={y.year}
                          style={{
                            position: "absolute",
                            left: `${y.pct}%`,
                            fontSize: 11,
                            color: colors.textMuted,
                            fontWeight: 600,
                            transform: "translateX(-50%)",
                          }}
                        >
                          {y.year}
                        </span>
                      ))}
                    </div>
                    {/* Contract bars */}
                    {activeC.map((c, i) => {
                      const u = units.find((u) => u.id === c.unit_id);
                      const dl = daysUntil(c.end_date);
                      const left = toPercent(c.start_date);
                      const right = toPercent(c.end_date);
                      const width = right - left;
                      const barColor =
                        dl <= 0
                          ? `linear-gradient(90deg, ${colors.red}88, ${colors.red}44)`
                          : dl <= 60
                          ? `linear-gradient(90deg, ${colors.yellow}88, ${colors.yellow}44)`
                          : `linear-gradient(90deg, ${colors.green}88, ${colors.green}44)`;
                      const borderColor =
                        dl <= 0
                          ? colors.red
                          : dl <= 60
                          ? colors.yellow
                          : colors.green;
                      return (
                        <div
                          key={c.id}
                          style={{
                            position: "relative",
                            height: 32,
                            marginBottom: 10,
                          }}
                        >
                          {/* Unit code label */}
                          <span
                            style={{
                              position: "absolute",
                              left: -80,
                              top: 6,
                              width: 70,
                              textAlign: "right",
                              fontSize: 12,
                              fontWeight: 700,
                              color: borderColor,
                            }}
                          >
                            {u?.code || "?"}
                          </span>
                          {/* Background track */}
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              top: 4,
                              height: 24,
                              background: `${colors.bg}`,
                              borderRadius: 6,
                            }}
                          />
                          {/* Bar */}
                          <div
                            style={{
                              position: "absolute",
                              left: `${left}%`,
                              width: `${width}%`,
                              top: 4,
                              height: 24,
                              background: barColor,
                              border: `1px solid ${borderColor}40`,
                              borderRadius: 6,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: borderColor,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.end_date}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {/* Today line */}
                    {todayPct >= 0 && todayPct <= 100 && (
                      <div
                        style={{
                          position: "absolute",
                          left: `calc(80px + ${todayPct}% - ${
                            todayPct * 0.8
                          }px)`,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          background: colors.red,
                          opacity: 0.8,
                          zIndex: 5,
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: -2,
                            left: -12,
                            fontSize: 9,
                            fontWeight: 800,
                            color: colors.red,
                          }}
                        >
                          HOY
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div style={S.card}>
              <h3
                style={{
                  margin: "0 0 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.textMuted,
                }}
              >
                Todos los Contratos
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {contracts.map((c) => {
                  const t = tenants.find((t) => t.id === c.tenant_id);
                  const u = units.find((u) => u.id === c.unit_id);
                  const dl = daysUntil(c.end_date);
                  return (
                    <div
                      key={c.id}
                      onClick={() =>
                        isAdmin &&
                        setModal({ type: "contract", data: { ...c } })
                      }
                      style={{
                        display: isMobile ? "flex" : "grid",
                        flexDirection: isMobile ? "column" : undefined,
                        gridTemplateColumns: isMobile
                          ? undefined
                          : "70px 1fr 130px 90px 100px",
                        alignItems: isMobile ? "flex-start" : "center",
                        gap: isMobile ? 4 : 10,
                        padding: isMobile ? "10px 12px" : "12px 14px",
                        background: `${colors.bg}80`,
                        borderRadius: 10,
                        cursor: isAdmin ? "pointer" : "default",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: isMobile ? "100%" : "auto",
                          ...(isMobile ? {} : { display: "contents" }),
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: colors.primary,
                          }}
                        >
                          {u?.code}
                        </span>
                        {isMobile && (
                          <Badge
                            type={
                              dl <= 0
                                ? "expired"
                                : dl <= 60
                                ? "expiring"
                                : "active"
                            }
                            text={
                              dl <= 0
                                ? "Vencido"
                                : dl <= 60
                                ? "Por vencer"
                                : "Vigente"
                            }
                          />
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {t?.name}
                        </div>
                        <div style={{ fontSize: 11, color: colors.textMuted }}>
                          {fmtDate(c.start_date)} → {fmtDate(c.end_date)}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          textAlign: isMobile ? "left" : "right",
                        }}
                      >
                        {$fmt(c.monthly_rent)}/mes
                        {isMobile && (
                          <span
                            style={{
                              fontSize: 11,
                              color:
                                dl <= 60 ? colors.yellow : colors.textMuted,
                              marginLeft: 8,
                            }}
                          >
                            {dl > 0 ? `${dl}d` : "Vencido"}
                          </span>
                        )}
                      </span>
                      {!isMobile && (
                        <span
                          style={{
                            fontSize: 12,
                            color: dl <= 60 ? colors.yellow : colors.textMuted,
                            textAlign: "right",
                          }}
                        >
                          {dl > 0 ? `${dl}d` : "Vencido"}
                        </span>
                      )}
                      {!isMobile && (
                        <Badge
                          type={
                            dl <= 0
                              ? "expired"
                              : dl <= 60
                              ? "expiring"
                              : "active"
                          }
                          text={
                            dl <= 0
                              ? "Vencido"
                              : dl <= 60
                              ? "Por vencer"
                              : "Vigente"
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ GASTOS TAB ═══════════ */}
        {activeTab === "maintenance" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {[
                { k: "all", l: "Todos" },
                { k: "scheduled", l: "Programados" },
                { k: "completed", l: "Completados" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setMaintFilter(f.k)}
                  style={{
                    background:
                      maintFilter === f.k
                        ? `${colors.primary}20`
                        : "transparent",
                    border:
                      maintFilter === f.k
                        ? `1px solid ${colors.primary}40`
                        : `1px solid ${colors.border}`,
                    color:
                      maintFilter === f.k
                        ? colors.primaryHover
                        : colors.textMuted,
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {f.l}
                </button>
              ))}
              <div style={{ flex: 1 }} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 11,
                      color: colors.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    CATEGORÍA:
                  </span>
                  <select
                    value={maintCat}
                    onChange={(e) => setMaintCat(e.target.value)}
                    style={{
                      ...S.input,
                      width: 180,
                      padding: "6px 10px",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    <option value="all">Todas</option>
                    {[...new Set(maintenance.map((m) => m.category))]
                      .sort()
                      .map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 11,
                      color: colors.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    MES:
                  </span>
                  <button
                    onClick={() => setMaintMonth("all")}
                    style={{
                      background:
                        maintMonth === "all"
                          ? `${colors.primary}20`
                          : "transparent",
                      border:
                        maintMonth === "all"
                          ? `1px solid ${colors.primary}40`
                          : `1px solid ${colors.border}`,
                      color:
                        maintMonth === "all"
                          ? colors.primaryHover
                          : colors.textMuted,
                      padding: "4px 10px",
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Todos
                  </button>
                  <input
                    type="month"
                    value={maintMonth === "all" ? "" : maintMonth}
                    onChange={(e) => setMaintMonth(e.target.value)}
                    style={{
                      ...S.input,
                      width: 160,
                      padding: "6px 10px",
                      fontSize: 12,
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 14,
              }}
            >
              {/* 12-month expenses chart */}
              <div style={S.card}>
                <h3
                  style={{
                    margin: "0 0 14px",
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.textMuted,
                  }}
                >
                  Gastos Mensuales (últimos 12 meses)
                </h3>
                {(() => {
                  const months = [];
                  const now = new Date();
                  for (let i = 11; i >= 0; i--) {
                    const d = new Date(
                      now.getFullYear(),
                      now.getMonth() - i,
                      1
                    );
                    const key = `${d.getFullYear()}-${String(
                      d.getMonth() + 1
                    ).padStart(2, "0")}`;
                    const label = d
                      .toLocaleDateString("es-MX", { month: "short" })
                      .replace(".", "");
                    const total = maintenance
                      .filter(
                        (m) =>
                          (m.scheduled_date || m.completed_date || "").slice(
                            0,
                            7
                          ) === key
                      )
                      .filter((m) => m.status !== "scheduled")
                      .reduce((s, m) => s + m.cost, 0);
                    months.push({ key, label, total });
                  }
                  const maxVal = Math.max(...months.map((m) => m.total), 1);
                  const currentKey = `${now.getFullYear()}-${String(
                    now.getMonth() + 1
                  ).padStart(2, "0")}`;
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 6,
                        height: 180,
                      }}
                    >
                      {months.map((m) => (
                        <div
                          key={m.key}
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              color: colors.textMuted,
                              fontWeight: 600,
                            }}
                          >
                            {m.total > 0
                              ? privacyMode
                                ? "$••"
                                : `$${Math.round(m.total / 1000)}k`
                              : ""}
                          </span>
                          <div
                            style={{
                              width: "100%",
                              maxWidth: 40,
                              borderRadius: 6,
                              height: `${Math.max(
                                (m.total / maxVal) * 140,
                                4
                              )}px`,
                              background:
                                m.key === currentKey
                                  ? `linear-gradient(180deg, ${colors.yellow}, #b45309)`
                                  : `${colors.border}`,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 10,
                              color:
                                m.key === currentKey
                                  ? colors.yellow
                                  : colors.textMuted,
                              fontWeight: m.key === currentKey ? 700 : 400,
                            }}
                          >
                            {m.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Cost by category (filtered by month) */}
              <div style={S.card}>
                <h3
                  style={{
                    margin: "0 0 14px",
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.textMuted,
                  }}
                >
                  Gasto por Categoría —{" "}
                  {maintMonth === "all"
                    ? "Todos los meses"
                    : new Date(maintMonth + "-15").toLocaleDateString("es-MX", {
                        month: "long",
                        year: "numeric",
                      })}
                </h3>
                {(() => {
                  const filtered = maintenance
                    .filter(
                      (m) =>
                        maintMonth === "all" ||
                        (m.scheduled_date || m.completed_date || "").slice(
                          0,
                          7
                        ) === maintMonth
                    )
                    .filter(
                      (m) => maintCat === "all" || m.category === maintCat
                    )
                    .filter((m) => m.status !== "scheduled");
                  if (filtered.length === 0)
                    return (
                      <div
                        style={{
                          color: colors.textMuted,
                          fontSize: 13,
                          textAlign: "center",
                          padding: 20,
                        }}
                      >
                        Sin gastos en este mes
                      </div>
                    );
                  const cats = {};
                  filtered.forEach((m) => {
                    cats[m.category] = (cats[m.category] || 0) + m.cost;
                  });
                  const maxCat = Math.max(...Object.values(cats));
                  const totalMonth = filtered.reduce((s, m) => s + m.cost, 0);
                  const catCols = [
                    colors.primary,
                    colors.green,
                    colors.yellow,
                    colors.red,
                    colors.blue,
                    colors.purple,
                  ];
                  return (
                    <>
                      {Object.entries(cats)
                        .sort((a, b) => b[1] - a[1])
                        .map(([cat, cost], i) => (
                          <div key={cat} style={{ marginBottom: 10 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 12,
                                marginBottom: 3,
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{cat}</span>
                              <span
                                style={{
                                  color: catCols[i % catCols.length],
                                  fontWeight: 700,
                                }}
                              >
                                {$fmt(cost)}{" "}
                                <span style={{ fontSize: 10, opacity: 0.7 }}>
                                  (
                                  {totalMonth > 0
                                    ? ((cost / totalMonth) * 100).toFixed(1)
                                    : 0}
                                  %)
                                </span>
                              </span>
                            </div>
                            <div
                              style={{
                                height: 6,
                                background: `${colors.text}08`,
                                borderRadius: 3,
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${(cost / maxCat) * 100}%`,
                                  background: `linear-gradient(90deg, ${
                                    catCols[i % catCols.length]
                                  }60, ${catCols[i % catCols.length]})`,
                                  borderRadius: 3,
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      <div
                        style={{
                          marginTop: 14,
                          fontSize: 12,
                          color: colors.textMuted,
                          borderTop: `1px solid ${colors.border}`,
                          paddingTop: 10,
                        }}
                      >
                        Total del mes:{" "}
                        <strong style={{ color: colors.purple }}>
                          {$fmt(totalMonth)}
                        </strong>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div style={S.card}>
              <h3
                style={{
                  margin: "0 0 14px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.textMuted,
                }}
              >
                Registro
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(maintFilter === "all"
                  ? maintenance
                  : maintenance.filter((m) => m.status === maintFilter)
                )
                  .filter(
                    (m) =>
                      maintMonth === "all" ||
                      (m.scheduled_date || m.completed_date || "").slice(
                        0,
                        7
                      ) === maintMonth
                  )
                  .filter((m) => maintCat === "all" || m.category === maintCat)
                  .sort((a, b) =>
                    (b.scheduled_date || "").localeCompare(
                      a.scheduled_date || ""
                    )
                  )
                  .map((m) => {
                    const u = units.find((u) => u.id === m.unit_id);
                    return (
                      <div
                        key={m.id}
                        onClick={() =>
                          isAdmin &&
                          setModal({ type: "maintenance", data: { ...m } })
                        }
                        style={{
                          display: isMobile ? "flex" : "grid",
                          flexDirection: isMobile ? "column" : undefined,
                          gridTemplateColumns: isMobile
                            ? undefined
                            : "80px 90px 1fr 100px 100px",
                          alignItems: isMobile ? "flex-start" : "center",
                          gap: isMobile ? 4 : 10,
                          padding: isMobile ? "10px 12px" : "10px 14px",
                          background: `${colors.bg}80`,
                          borderRadius: 8,
                          borderLeft: `3px solid ${
                            {
                              scheduled: colors.purple,
                              in_progress: colors.blue,
                              completed: colors.green,
                              cancelled: colors.red,
                            }[m.status]
                          }`,
                          cursor: isAdmin ? "pointer" : "default",
                        }}
                      >
                        <span style={{ fontSize: 11, color: colors.textMuted }}>
                          {fmtDate(m.scheduled_date)}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>
                          {m.category}
                        </span>
                        <div>
                          <div style={{ fontSize: 12 }}>{m.description}</div>
                          <div
                            style={{ fontSize: 10, color: colors.textMuted }}
                          >
                            {m.vendor} · {u?.code || "Área Común"}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            textAlign: "right",
                          }}
                        >
                          {$fmt(m.cost)}
                        </span>
                        <Badge
                          type={m.status}
                          text={
                            {
                              scheduled: "Programado",
                              in_progress: "En proceso",
                              completed: "Completado",
                              cancelled: "Cancelado",
                            }[m.status]
                          }
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ TRABAJOS DE MTO ═══════════ */}
        {activeTab === "workorders" &&
          (() => {
            const pendingJobs = workorders.filter(
              (w) => w.status === "pending"
            );
            const completedJobs = workorders.filter(
              (w) => w.status === "completed" && w.completed_date
            );
            const jobsWithTime = completedJobs.filter(
              (w) => w.requested_date && w.completed_date
            );
            const avgResponseDays =
              jobsWithTime.length > 0
                ? (
                    jobsWithTime.reduce((sum, w) => {
                      const diff =
                        (new Date(w.completed_date) -
                          new Date(w.requested_date)) /
                        (1000 * 60 * 60 * 24);
                      return sum + Math.max(0, diff);
                    }, 0) / jobsWithTime.length
                  ).toFixed(1)
                : "—";
            return (
              <div>
                {/* KPIs de trabajos */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 1fr"
                      : "repeat(3, 1fr)",
                    gap: isMobile ? 8 : 14,
                    marginBottom: isMobile ? 14 : 20,
                  }}
                >
                  <KPI
                    isDark={isDark}
                    icon="⏱️"
                    label="Tiempo Prom. Respuesta"
                    value={
                      avgResponseDays === "—" ? "—" : `${avgResponseDays} días`
                    }
                    sub={`De ${jobsWithTime.length} trabajos completados`}
                    color={colors.primary}
                  />
                  <KPI
                    isDark={isDark}
                    icon="🔧"
                    label="Pendientes"
                    value={pendingJobs.length}
                    sub="Trabajos por realizar"
                    color={colors.yellow}
                  />
                  <KPI
                    isDark={isDark}
                    icon="✅"
                    label="Completados"
                    value={completedJobs.length}
                    sub="Trabajos realizados"
                    color={colors.green}
                  />
                </div>

                {/* Pendientes */}
                <div style={S.card}>
                  <h3
                    style={{
                      margin: "0 0 14px",
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.text,
                    }}
                  >
                    Trabajos Pendientes
                  </h3>
                  {pendingJobs.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        color: colors.textMuted,
                        padding: 20,
                        fontSize: 13,
                      }}
                    >
                      No hay trabajos pendientes 🎉
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {pendingJobs
                        .sort((a, b) => {
                          const pOrder = { alta: 0, media: 1, baja: 2 };
                          return (
                            (pOrder[a.priority] ?? 1) -
                              (pOrder[b.priority] ?? 1) ||
                            (a.requested_date || "").localeCompare(
                              b.requested_date || ""
                            )
                          );
                        })
                        .map((w) => {
                          const u = units.find((uu) => uu.id === w.unit_id);
                          const daysSince = w.requested_date
                            ? Math.max(
                                0,
                                Math.floor(
                                  (new Date() - new Date(w.requested_date)) /
                                    (1000 * 60 * 60 * 24)
                                )
                              )
                            : null;
                          const prioColors = {
                            alta: colors.red,
                            media: colors.yellow,
                            baja: colors.green,
                          };
                          const prioLabels = {
                            alta: "🔴 Alta",
                            media: "🟡 Media",
                            baja: "🟢 Baja",
                          };
                          return (
                            <div
                              key={w.id}
                              style={{
                                background: colors.bgInput,
                                borderRadius: 12,
                                padding: isMobile ? 12 : 16,
                                border: `1px solid ${colors.border}`,
                                borderLeft: `4px solid ${
                                  prioColors[w.priority] || colors.border
                                }`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 14,
                                      color: colors.text,
                                      marginBottom: 4,
                                    }}
                                  >
                                    {w.description}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: colors.textMuted,
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 8,
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        background: `${colors.primary}15`,
                                        color: colors.primary,
                                        padding: "2px 8px",
                                        borderRadius: 6,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {w.category}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color:
                                          prioColors[w.priority] ||
                                          colors.textMuted,
                                      }}
                                    >
                                      {prioLabels[w.priority] || w.priority}
                                    </span>
                                    {u && <span>📍 {u.code}</span>}
                                    {w.is_common_area && (
                                      <span>📍 Área común</span>
                                    )}
                                    {w.requested_date && (
                                      <span>
                                        📅{" "}
                                        {new Date(
                                          w.requested_date + "T12:00"
                                        ).toLocaleDateString("es-MX", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </span>
                                    )}
                                    {daysSince !== null && daysSince > 0 && (
                                      <span
                                        style={{
                                          color:
                                            daysSince > 7
                                              ? colors.red
                                              : colors.yellow,
                                          fontWeight: 600,
                                        }}
                                      >
                                        ⏱️ {daysSince} día
                                        {daysSince !== 1 ? "s" : ""} esperando
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {userRole !== "readonly" && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 6,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {isAdmin && (
                                      <button
                                        onClick={() =>
                                          setModal({
                                            type: "workorder",
                                            data: { ...w },
                                          })
                                        }
                                        style={{
                                          ...S.btn,
                                          padding: "8px 12px",
                                          fontSize: 12,
                                          border: `1px solid ${colors.border}`,
                                        }}
                                      >
                                        ✏️
                                      </button>
                                    )}
                                    <button
                                      onClick={() =>
                                        setModal({
                                          type: "complete_workorder",
                                          data: {
                                            ...w,
                                            completion_notes: w.notes || "",
                                          },
                                        })
                                      }
                                      style={{
                                        ...S.btn,
                                        ...S.btnPrimary,
                                        padding: "8px 16px",
                                        fontSize: 12,
                                      }}
                                    >
                                      ✓ Realizado
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Historial completados */}
                <div style={{ ...S.card, marginTop: 14 }}>
                  <h3
                    style={{
                      margin: "0 0 14px",
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.text,
                    }}
                  >
                    Historial de Trabajos Completados
                  </h3>
                  {completedJobs.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        color: colors.textMuted,
                        padding: 20,
                        fontSize: 13,
                      }}
                    >
                      Aún no hay trabajos completados
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {completedJobs
                        .sort((a, b) =>
                          (b.completed_date || "").localeCompare(
                            a.completed_date || ""
                          )
                        )
                        .map((w) => {
                          const u = units.find((uu) => uu.id === w.unit_id);
                          const respDays =
                            w.requested_date && w.completed_date
                              ? Math.max(
                                  0,
                                  Math.floor(
                                    (new Date(w.completed_date) -
                                      new Date(w.requested_date)) /
                                      (1000 * 60 * 60 * 24)
                                  )
                                )
                              : null;
                          return (
                            <div
                              key={w.id}
                              style={{
                                background: colors.bgInput,
                                borderRadius: 10,
                                padding: isMobile ? 10 : 12,
                                border: `1px solid ${colors.border}`,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                                cursor: isAdmin ? "pointer" : "default",
                              }}
                              onClick={() =>
                                isAdmin &&
                                setModal({ type: "workorder", data: { ...w } })
                              }
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: colors.text,
                                  }}
                                >
                                  {w.description}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: colors.textMuted,
                                    marginTop: 2,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 6,
                                  }}
                                >
                                  <span
                                    style={{
                                      background: `${colors.primary}15`,
                                      color: colors.primary,
                                      padding: "1px 6px",
                                      borderRadius: 5,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {w.category}
                                  </span>
                                  {u && <span>{u.code}</span>}
                                  {w.is_common_area && <span>Área común</span>}
                                  <span>
                                    ✓{" "}
                                    {new Date(
                                      w.completed_date + "T12:00"
                                    ).toLocaleDateString("es-MX", {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>
                                  {w.notes && <span>📝 {w.notes}</span>}
                                </div>
                              </div>
                              {respDays !== null && (
                                <div
                                  style={{
                                    background:
                                      respDays === 0
                                        ? `${colors.green}20`
                                        : respDays <= 3
                                        ? `${colors.green}15`
                                        : respDays <= 7
                                        ? `${colors.yellow}20`
                                        : `${colors.red}20`,
                                    color:
                                      respDays === 0
                                        ? colors.green
                                        : respDays <= 3
                                        ? colors.green
                                        : respDays <= 7
                                        ? colors.yellow
                                        : colors.red,
                                    padding: "4px 10px",
                                    borderRadius: 8,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {respDays === 0
                                    ? "Mismo día"
                                    : `${respDays} día${
                                        respDays !== 1 ? "s" : ""
                                      }`}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        {/* ═══════════ PROYECTOS TAB ═══════════ */}
        {activeTab === "projects" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Project KPIs */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
                gap: isMobile ? 8 : 14,
              }}
            >
              <KPI
                isDark={isDark}
                icon="📋"
                label="Planeados"
                value={projects.filter((p) => p.status === "planeado").length}
                sub="Por comenzar"
                color={colors.purple}
              />
              <KPI
                isDark={isDark}
                icon="🚧"
                label="En Progreso"
                value={
                  projects.filter((p) => p.status === "en_progreso").length
                }
                sub="Actualmente"
                color={colors.blue}
              />
              <KPI
                isDark={isDark}
                icon="✅"
                label="Completados"
                value={projects.filter((p) => p.status === "completado").length}
                sub={`Costo total: ${$fmt(
                  projects
                    .filter((p) => p.status === "completado")
                    .reduce((s, p) => s + (p.estimated_cost || 0), 0)
                )}`}
                color={colors.green}
              />
            </div>

            {/* Project list */}
            {projects.length === 0 ? (
              <div
                style={{
                  ...S.card,
                  textAlign: "center",
                  padding: 40,
                  color: colors.textDim,
                }}
              >
                No hay proyectos registrados
              </div>
            ) : (
              projects
                .sort((a, b) => {
                  const order = { en_progreso: 0, planeado: 1, completado: 2 };
                  return (
                    (order[a.status] ?? 1) - (order[b.status] ?? 1) ||
                    (b.created_at || "").localeCompare(a.created_at || "")
                  );
                })
                .map((proj) => {
                  const statusLabel =
                    {
                      planeado: "Planeado",
                      en_progreso: "En Progreso",
                      completado: "Completado",
                    }[proj.status] || proj.status;
                  const statusType =
                    {
                      planeado: "scheduled",
                      en_progreso: "pending",
                      completado: "paid",
                    }[proj.status] || "pending";
                  const prioLabel =
                    { alta: "🔴 Alta", media: "🟡 Media", baja: "🟢 Baja" }[
                      proj.priority
                    ] || proj.priority;
                  return (
                    <div
                      key={proj.id}
                      onClick={() =>
                        isAdmin &&
                        setModal({ type: "project", data: { ...proj } })
                      }
                      style={{
                        ...S.card,
                        cursor: isAdmin ? "pointer" : "default",
                        transition: "all 0.15s",
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? 12 : 20,
                        alignItems: isMobile ? "flex-start" : "center",
                      }}
                      onMouseEnter={(e) =>
                        isAdmin &&
                        (e.currentTarget.style.borderColor =
                          colors.primary + "60")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.borderColor = colors.border)
                      }
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: colors.text,
                            }}
                          >
                            {proj.name}
                          </span>
                          <Badge type={statusType} text={statusLabel} />
                        </div>
                        {proj.description && (
                          <div
                            style={{
                              fontSize: 13,
                              color: colors.textMuted,
                              marginBottom: 6,
                            }}
                          >
                            {proj.description}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: 16,
                            flexWrap: "wrap",
                            fontSize: 12,
                            color: colors.textDim,
                          }}
                        >
                          <span>Prioridad: {prioLabel}</span>
                          {proj.estimated_start && (
                            <span>Inicio: {fmtDate(proj.estimated_start)}</span>
                          )}
                          {proj.estimated_cost > 0 && (
                            <span>Costo est.: {$fmt(proj.estimated_cost)}</span>
                          )}
                        </div>
                        {proj.notes && (
                          <div
                            style={{
                              fontSize: 12,
                              color: colors.textMuted,
                              marginTop: 6,
                              fontStyle: "italic",
                            }}
                          >
                            {proj.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {renderModal()}

      <style>{`
        @keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${colors.border}; border-radius: 3px; }
        select option { background: ${colors.bgCard}; color: ${colors.text}; }
        @media (max-width: 768px) {
          ::-webkit-scrollbar { display: none; }
          input, select, button { font-size: 16px !important; }
        }
      `}</style>
    </div>
  );
}